import React, { useState, useEffect, useRef, useCallback } from "react";
import * as fabric from "fabric";
import { useDispatch, useSelector } from "react-redux";
import { removeLayer, setActiveLayerId, setSelectedObjectProps, setDesignRegions, setRegionColor, removeRegion, clearRegions, setCurrentAssetId, setCurrentDesign, syncGeneratedLayers, setLayerVisible, updateLayer, setLayers, setIsGenerating, setIsRestoringDesign, setIsSavingDesign, RegionData, Layer } from "../../../reducers/editorSlice";
import { RootState } from "../../../app/store";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
import { useGetDesignsQuery, useLazyGetDesignQuery, useUpdateDesignMutation, useDeleteDesignMutation, useUploadMediaMutation, useExportDesignMutation, useLazyGetDesignExportQuery, useSaveDesignFinalMutation } from "../../../services/auth";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { clampTextStyleProps, getTextStyleLimits } from "../textStyleLimits";
import Loader from '../../../constants/Loader';
import { API_URL, STORAGE_KEYS } from "../../../constants";

const incrementStateValue = (value: number) => value + 1;
const editorTooltipProps = {
  arrow: true,
  placement: 'bottom' as const,
  enterDelay: 150,
  enterNextDelay: 80,
  classes: { popper: 'editor_tooltip_popper', tooltip: 'editor_tooltip' },
};

const ZOOM_OPTIONS = [50, 75, 100, 125, 150, 200];

interface DashboardProps {
  activeOverlay?: string;
}

function handleDesignResponse(data: any) {
  const design = {
    id: data.design_id || data.asset_id,
    title: data.title,
    status: data.status,
    seed: data.seed,
    version: data.version,
    previewUrl: data.preview_url || data.source_url,
    isClaimed: data.is_claimed,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    canvas: data.canvas || { width: data.width, height: data.height },
  };

  let parsedRegions = data.regions || [];
  if (typeof parsedRegions === 'string') {
    try {
      parsedRegions = JSON.parse(parsedRegions);
    } catch (e) {
      console.error("Failed to parse regions JSON:", e);
      parsedRegions = [];
    }
  }

  let parsedLayers = data.layers || [];
  if (typeof parsedLayers === 'string') {
    try {
      parsedLayers = JSON.parse(parsedLayers);
    } catch (e) {
      console.error("Failed to parse layers JSON:", e);
      parsedLayers = [];
    }
  }

  const layers = parsedLayers.map((layer: any) => {
    let parsedLayerRegions = layer.regions || [];
    if (typeof parsedLayerRegions === 'string') {
      try {
        parsedLayerRegions = JSON.parse(parsedLayerRegions);
      } catch (e) {
        console.error("Failed to parse layer regions JSON:", e);
        parsedLayerRegions = [];
      }
    }

    if ((!parsedLayerRegions || parsedLayerRegions.length === 0) && layer.type === 'region-image') {
      // If the backend didn't supply regions in the layer directly, fallback to top-level regions.
      parsedLayerRegions = parsedRegions;
    }

    let parsedTransform = layer.transform || {};
    if (typeof parsedTransform === 'string') {
      try {
        parsedTransform = JSON.parse(parsedTransform);
      } catch (e) {
        console.error("Failed to parse layer transform JSON:", e);
        parsedTransform = {};
      }
    }

    return {
      id: layer.id || layer.object_id,
      object_id: layer.object_id || layer.id,
      type: layer.type,
      zIndex: layer.z_index ?? layer.zIndex,
      visible: layer.visible,
      locked: layer.locked,
      transform: parsedTransform,
      fill: layer.fill ?? null,
      radius: layer.radius ?? layer.border_radius ?? null,
      opacity: layer.opacity ?? 1,
      width: layer.width ?? null,
      height: layer.height ?? null,
      assetId: layer.asset_id ?? layer.assetId ?? null,
      regions: parsedLayerRegions,
      text: layer.text ?? null,
      fontFamily: layer.fontFamily ?? layer.font_family ?? null,
      fontSize: layer.fontSize ?? layer.font_size ?? null,
      textAlign: layer.textAlign ?? layer.text_align ?? null,
      src: layer.src ?? layer.image_url ?? layer.source_url ?? layer.media_file_url ?? layer.url ?? layer.mediaFileUrl ?? null,
      mediaId: layer.media_id ?? layer.mediaId ?? null,
      blendMode: layer.blend_mode ?? layer.blendMode ?? null,
    };
  });



  const regions = parsedRegions.map((region: any) => {
    const origColor = region.original_color || region.originalColor;
    const currentColor = region.current_color || region.color || region.custom_color || origColor;
    return {
      id: region.region_id || region.id,
      name: region.name,
      originalColor: origColor,
      currentColor: currentColor,
      color: currentColor,
      maskUrl: region.mask_url || region.maskUrl,
      bounds: region.bounds,
    };
  });

  const regionMap = Object.fromEntries(
    regions.map((r: any) => [r.id, r])
  );

  return { design, layers, regions, regionMap };
}

const designs = [
  "/static/images/enhance_img.png",
  "/static/images/design1.png",
  "/static/images/design2.png",
  "/static/images/design3.png",
  "/static/images/design4.png",
  "/static/images/design5.png",
  "/static/images/design6.png",
];

const DEFAULT_LOGICAL_SIZE = { width: 2048, height: 2048 };

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

async function fetchBlobWithTimeout(url: string, timeoutMs: number): Promise<Blob> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      mode: 'cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`preview GET ${res.status}`);
    const blob = await res.blob();
    if (!blob || blob.size < 32) throw new Error('preview empty');
    return blob;
  } finally {
    window.clearTimeout(timer);
  }
}

async function fetchPreviewBlob(url: string): Promise<Blob> {
  const attempts = [
    { wait: 0, timeout: 25000 },
    { wait: 800, timeout: 12000 },
    { wait: 1500, timeout: 12000 },
    { wait: 3000, timeout: 15000 },
    { wait: 5000, timeout: 20000 },
  ];
  let lastErr: unknown;
  for (let i = 0; i < attempts.length; i += 1) {
    if (attempts[i].wait) await sleep(attempts[i].wait);
    try {
      return await fetchBlobWithTimeout(url, attempts[i].timeout);
    } catch (err) {
      lastErr = err;
      console.warn('[Canvas] preview fetch retry', i + 1, err);
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('preview fetch failed');
}

async function loadFabricImage(url: string): Promise<fabric.Image> {
  const ImageClass = (fabric as any).FabricImage || fabric.Image;
  try {
    return await withTimeout(
      ImageClass.fromURL(url, { crossOrigin: 'anonymous' }),
      15000,
      'image fromURL timed out',
    );
  } catch {
    return await withTimeout(
      ImageClass.fromURL(url),
      15000,
      'image fromURL timed out',
    );
  }
}

function isDummyMaskSvg(svg: string): boolean {
  const compact = svg.replace(/\s+/g, ' ').toLowerCase();
  return compact.includes('opacity="0"') || compact.includes("opacity='0'") || compact.includes('opacity:0');
}

function loadHtmlImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = window.setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      reject(new Error(`Image load timed out: ${url}`));
    }, 15000);
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error(`Failed to load mask image: ${url}`));
    };
    img.src = url;
  });
}

function isEdgeBackgroundPixel(
  r: number,
  g: number,
  b: number,
  a: number,
  seed: { r: number; g: number; b: number },
) {
  if (a < 10) return true;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  if (lum > 42) return false;
  const dist = Math.abs(r - seed.r) + Math.abs(g - seed.g) + Math.abs(b - seed.b);
  return dist < 36 || lum < 24;
}

function knockOutBakedBackground(source: CanvasImageSource, width: number, height: number): HTMLCanvasElement | null {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const sample = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    return { r: data[i], g: data[i + 1], b: data[i + 2], a: data[i + 3] };
  };
  const corners = [sample(0, 0), sample(w - 1, 0), sample(0, h - 1), sample(w - 1, h - 1)];
  const seed = {
    r: Math.round(corners.reduce((sum, p) => sum + p.r, 0) / 4),
    g: Math.round(corners.reduce((sum, p) => sum + p.g, 0) / 4),
    b: Math.round(corners.reduce((sum, p) => sum + p.b, 0) / 4),
  };
  const seedLum = 0.2126 * seed.r + 0.7152 * seed.g + 0.0722 * seed.b;
  if (seedLum > 48) return null;

  const visited = new Uint8Array(w * h);
  const stack = new Int32Array(w * h);
  let top = 0;
  const enqueue = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const idx = y * w + x;
    if (visited[idx]) return;
    visited[idx] = 1;
    const i = idx * 4;
    if (!isEdgeBackgroundPixel(data[i], data[i + 1], data[i + 2], data[i + 3], seed)) return;
    stack[top] = idx;
    top += 1;
  };

  for (let x = 0; x < w; x += 1) {
    enqueue(x, 0);
    enqueue(x, h - 1);
  }
  for (let y = 0; y < h; y += 1) {
    enqueue(0, y);
    enqueue(w - 1, y);
  }

  while (top > 0) {
    top -= 1;
    const idx = stack[top];
    const i = idx * 4;
    data[i + 3] = 0;
    const x = idx % w;
    const y = (idx / w) | 0;
    enqueue(x + 1, y);
    enqueue(x - 1, y);
    enqueue(x, y + 1);
    enqueue(x, y - 1);
  }

  for (let idx = 0; idx < w * h; idx += 1) {
    const i = idx * 4;
    if (data[i + 3] === 0) continue;
    const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    if (lum > 28) continue;
    const neighbors = [
      idx - 1,
      idx + 1,
      idx - w,
      idx + w,
    ];
    const nextToHole = neighbors.some((n) => n >= 0 && n < w * h && data[n * 4 + 3] === 0);
    if (nextToHole) data[i + 3] = 0;
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function canvasToBlobUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to export transparent preview'));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });
}

function binarizeMaskCanvas(source: CanvasImageSource, logical: { width: number; height: number }) {
  const canvas = document.createElement('canvas');
  canvas.width = logical.width;
  canvas.height = logical.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  let covered = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    const inRegion = data[i + 3] > 12 && lum > 8;
    if (inRegion) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
      covered += 1;
    } else {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 0;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return covered > 0 ? canvas : null;
}

async function rasterizeRegionMask(maskUrl: string, logical: { width: number; height: number }) {
  const res = await fetch(maskUrl, { mode: 'cors' });
  if (!res.ok) throw new Error(`mask GET ${res.status}`);
  const blob = await res.blob();
  const isSvg = (blob.type || '').includes('svg') || /\.svg(\?|$)/i.test(maskUrl);

  if (isSvg) {
    const svgText = await blob.text();
    if (isDummyMaskSvg(svgText)) return { canvas: null, dummy: true, covered: false };
    const svgUrl = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }));
    try {
      const img = await loadHtmlImage(svgUrl);
      const canvas = binarizeMaskCanvas(img, logical);
      return { canvas, dummy: false, covered: !!canvas };
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  const pngUrl = URL.createObjectURL(blob);
  try {
    const img = await loadHtmlImage(pngUrl);
    const canvas = binarizeMaskCanvas(img, logical);
    return { canvas, dummy: false, covered: !!canvas };
  } finally {
    URL.revokeObjectURL(pngUrl);
  }
}

function applyRegionRecolors(
  original: CanvasImageSource,
  logical: { width: number; height: number },
  jobs: { mask: HTMLCanvasElement; color: string }[],
  deletedMasks: HTMLCanvasElement[] = [],
) {
  const out = document.createElement('canvas');
  out.width = logical.width;
  out.height = logical.height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.clearRect(0, 0, out.width, out.height);
  ctx.drawImage(original, 0, 0, out.width, out.height);
  if (jobs.length === 0 && deletedMasks.length === 0) return out.toDataURL('image/png');

  for (const job of jobs) {
    const slice = document.createElement('canvas');
    slice.width = out.width;
    slice.height = out.height;
    const sctx = slice.getContext('2d', { willReadFrequently: true });
    if (!sctx) continue;

    sctx.drawImage(original, 0, 0, slice.width, slice.height);
    sctx.globalCompositeOperation = 'destination-in';
    sctx.drawImage(job.mask, 0, 0, slice.width, slice.height);
    const clipped = sctx.getImageData(0, 0, slice.width, slice.height);
    sctx.globalCompositeOperation = 'color';
    sctx.fillStyle = job.color;
    sctx.fillRect(0, 0, slice.width, slice.height);
    const tinted = sctx.getImageData(0, 0, slice.width, slice.height);
    for (let i = 3; i < tinted.data.length; i += 4) {
      tinted.data[i] = clipped.data[i];
    }
    sctx.putImageData(tinted, 0, 0);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(job.mask, 0, 0, out.width, out.height);
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(slice, 0, 0);
  }

  for (const dMask of deletedMasks) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(dMask, 0, 0, out.width, out.height);
  }

  ctx.globalCompositeOperation = 'source-over';
  return out.toDataURL('image/png');
}

function isRegionCanvasLayer(obj: fabric.Object) {
  const t = String(obj.get('layerType') || '');
  return t === 'region' || t === 'region-image' || t === 'region-tint';
}

function getObjectId(obj: fabric.Object | null | undefined): string {
  if (!obj) return '';
  return String(obj.get('object_id') || obj.get('layerId') || '');
}

function findCanvasObjectById(canvas: fabric.Canvas, id: string): fabric.Object | undefined {
  if (!id) return undefined;
  return canvas.getObjects().find((obj) => getObjectId(obj) === id);
}

function stampObjectId(obj: fabric.Object, id: string) {
  obj.set({ object_id: id, layerId: id } as any);
}

function applyDesignLock(obj: fabric.Object) {
  obj.set({
    selectable: false,
    evented: false,
    hasControls: false,
    hasBorders: false,
    lockMovementX: true,
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
  });
}

const DEFAULT_CANVAS_TEXT = 'New Text';

function getTextContentError(value: unknown): string {
  const text = String(value ?? '');
  if (!text.length) return 'Text cannot be empty';
  if (!text.trim().length) return 'Text cannot contain only spaces';
  return '';
}

function isPlaceholderCanvasText(obj: fabric.Object) {
  const text = String((obj as any).text || '');
  return text === DEFAULT_CANVAS_TEXT;
}

function rememberValidCanvasText(obj: fabric.Object) {
  const text = String((obj as any).text || '');
  if (text.trim()) (obj as any)._lastValidText = text;
}

function restoreBlankCanvasText(obj: fabric.Object) {
  const previous = String((obj as any)._lastValidText || DEFAULT_CANVAS_TEXT);
  obj.set('text', previous.trim() ? previous : DEFAULT_CANVAS_TEXT);
  if (typeof (obj as any).initDimensions === 'function') {
    (obj as any).initDimensions();
  }
}

function bindTextEditingGuards(obj: fabric.Object) {
  if (!isCanvasTextObject(obj) || (obj as any).__textGuardsBound) return;
  (obj as any).__textGuardsBound = true;
  rememberValidCanvasText(obj);
  const textObj = obj as any;
  textObj.on('editing:entered' as never, () => {
    rememberValidCanvasText(obj);
    if (isPlaceholderCanvasText(obj) && typeof textObj.selectAll === 'function') {
      textObj.selectAll();
    }
  });
  textObj.on('editing:exited' as never, () => {
    const error = getTextContentError(textObj.text);
    if (error) {
      restoreBlankCanvasText(obj);
      toast.error(error, { toastId: 'canvas-text-empty' });
    } else {
      rememberValidCanvasText(obj);
    }
    const canvas = obj.canvas as fabric.Canvas | undefined;
    if (canvas) {
      applyTextImageLikeControls(obj);
      constrainObjectToArtboard(obj, {
        width: DEFAULT_LOGICAL_SIZE.width,
        height: DEFAULT_LOGICAL_SIZE.height,
      }, canvas);
      canvas.requestRenderAll();
      canvas.fire('object:modified', { target: obj });
    }
  });
}

function isIgnoredSelectionTarget(obj: fabric.Object) {
  const layerType = String(obj.get('layerType') || '');
  const id = getObjectId(obj);
  return layerType === 'background' || layerType === 'crop' || id === 'crop-rect';
}

const HISTORY_OBJECT_PROPS = [
  'layerId',
  'object_id',
  'layerType',
  'layerName',
  'mediaId',
  'mediaFileUrl',
  'borderRadius',
  'visible',
];

type EditorHistorySnapshot = {
  json: any;
  layers: { layerId: string; layerType: string; layerName: string; visible?: boolean }[];
  regionColors: Record<string, string>;
  regions?: RegionData[];
};

function takeEditorSnapshot(
  canvas: fabric.Canvas,
  layers: EditorHistorySnapshot['layers'],
  regionColors: Record<string, string>,
  regions?: RegionData[],
): EditorHistorySnapshot {
  const json = canvas.toObject(HISTORY_OBJECT_PROPS) as any;
  if (Array.isArray(json.objects)) {
    json.objects = json.objects.filter((obj: any) => {
      const t = String(obj?.layerType || '');
      const id = String(obj?.object_id || obj?.layerId || '');
      return t !== 'crop' && id !== 'crop-rect';
    });
  }
  return {
    json,
    layers: JSON.parse(JSON.stringify(layers || [])),
    regionColors: { ...(regionColors || {}) },
    regions: regions ? JSON.parse(JSON.stringify(regions)) : [],
  };
}

function commitActiveLayer(
  dispatch: ReturnType<typeof useDispatch>,
  activeLayerIdRef: { current: string | null },
  nextId: string | null,
) {
  const previous = activeLayerIdRef.current;
  if (previous !== nextId) {
    console.log('[ActiveLayer]', { previous, current: nextId });
  }
  activeLayerIdRef.current = nextId;
  dispatch(setActiveLayerId(nextId));
}

function collectObjectProps(obj: fabric.Object) {
  const props: any = {
    scale: obj.scaleX || 1,
    angle: obj.angle || 0,
    opacity: obj.opacity !== undefined ? obj.opacity : 1,
    left: obj.left || 0,
    top: obj.top || 0,
    scaledWidth: obj.getScaledWidth(),
    scaledHeight: obj.getScaledHeight(),
    originX: obj.originX,
    originY: obj.originY,
    borderRadius: Number(obj.get('borderRadius') || 0),
    blendMode: String((obj as any).globalCompositeOperation || 'source-over'),
    layerName: String(obj.get('layerName') || ''),
  };
  if (obj.type === 'image' || obj.get('layerType') === 'image') {
    props.src = String(obj.get('mediaFileUrl') || (typeof (obj as fabric.Image).getSrc === 'function' ? (obj as fabric.Image).getSrc() : ''));
  }
  if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') {
    const textObj = obj as any;
    props.text = textObj.text;
    props.fontFamily = textObj.fontFamily;
    props.fill = textObj.fill;
    props.textAlign = textObj.textAlign;
    props.fontWeight = textObj.fontWeight;
    props.fontStyle = textObj.fontStyle;
    props.fontSize = textObj.fontSize;
    props.charSpacing = textObj.charSpacing;
    props.lineHeight = textObj.lineHeight;
  }
  return props;
}

function activateCanvasLayer(
  canvas: fabric.Canvas,
  layerId: string,
  source: 'canvas' | 'layers-panel',
  ctx: {
    dispatch: ReturnType<typeof useDispatch>;
    activeLayerIdRef: { current: string | null };
    suppressSelectionClearedRef: { current: boolean };
  },
) {
  console.log('[LayerSelect]', { objectId: layerId, source });
  const obj = findCanvasObjectById(canvas, layerId);
  if (!obj || isIgnoredSelectionTarget(obj)) return;

  commitActiveLayer(ctx.dispatch, ctx.activeLayerIdRef, layerId);
  ctx.dispatch(setSelectedObjectProps(collectObjectProps(obj)));

  ctx.suppressSelectionClearedRef.current = true;
  canvas.setActiveObject(obj);
  canvas.requestRenderAll();
  console.log('[FabricSelection]', { objectId: getObjectId(canvas.getActiveObject()) || layerId });
  const release = () => {
    ctx.suppressSelectionClearedRef.current = false;
  };
  if (typeof queueMicrotask === 'function') queueMicrotask(release);
  else setTimeout(release, 0);
}

function applyObjectToolState(obj: fabric.Object, tool: ToolType) {
  const layerType = String(obj.get('layerType') || '');
  const layerId = getObjectId(obj);
  if (layerType === 'background') {
    applyDesignLock(obj);
    return;
  }
  if (layerType === 'region' || layerType === 'region-tint' || layerType === 'region-image') {
    obj.set({
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      perPixelTargetFind: true,
    } as any);
    return;
  }
  if (layerId === 'crop-rect' || layerType === 'crop') {
    obj.set({ selectable: tool === 'crop', evented: tool === 'crop' });
    return;
  }
  const interactive = tool === 'cursor';
  obj.set({
    selectable: interactive,
    evented: interactive,
    hasControls: interactive,
    hasBorders: interactive,
    lockMovementX: !interactive,
    lockMovementY: !interactive,
    lockScalingX: !interactive,
    lockScalingY: !interactive,
    lockRotation: !interactive,
    lockScalingFlip: true,
  });
  if (interactive && isCanvasTextObject(obj)) {
    applyTextImageLikeControls(obj);
  }
}

type ToolType = 'hand' | 'cursor' | 'crop';

function restackEditorLayers(canvas: fabric.Canvas, layerOrder?: string[]) {
  const bring = (obj: fabric.Object) => {
    if (typeof (canvas as any).bringObjectToFront === 'function') {
      (canvas as any).bringObjectToFront(obj);
    } else if (typeof (obj as any).bringToFront === 'function') {
      (obj as any).bringToFront();
    }
  };

  const objects = canvas.getObjects();
  const background = objects.filter((obj) => obj.get('layerType') === 'background');
  const crop = objects.filter((obj) => {
    const t = String(obj.get('layerType') || '');
    return t === 'crop' || getObjectId(obj) === 'crop-rect';
  });
  const skip = new Set([...background, ...crop]);
  const bases = objects
    .filter((obj) => isBaseStackLayer(obj) && !skip.has(obj))
    .sort((a, b) => {
      const ra = String(a.get('layerType') || '') === 'design' ? 0 : 1;
      const rb = String(b.get('layerType') || '') === 'design' ? 0 : 1;
      return ra - rb;
    });

  let overlays: fabric.Object[] = [];
  if (layerOrder && layerOrder.length) {
    const used = new Set<string>();
    layerOrder.forEach((id) => {
      const obj = findCanvasObjectById(canvas, id);
      if (!obj || skip.has(obj) || isBaseStackLayer(obj)) return;
      overlays.push(obj);
      used.add(id);
    });
    objects.forEach((obj) => {
      if (skip.has(obj) || isBaseStackLayer(obj) || used.has(getObjectId(obj))) return;
      overlays.push(obj);
    });
  } else {
    overlays = objects.filter((obj) => !skip.has(obj) && !isBaseStackLayer(obj));
  }

  [...background, ...bases, ...overlays, ...crop].forEach(bring);
}

function isUploadImageLayer(obj: fabric.Object | undefined | null): obj is fabric.Image {
  if (!obj) return false;
  return String(obj.get('layerType') || '') === 'image';
}

function isEditableImageLayer(obj: fabric.Object | undefined | null): obj is fabric.Image {
  if (!obj) return false;
  const t = String(obj.get('layerType') || '');
  return t === 'image' || t === 'design';
}

function isBaseStackLayer(obj: fabric.Object) {
  const t = String(obj.get('layerType') || '');
  return t === 'design' || t === 'region' || t === 'region-image' || t === 'region-tint';
}

function applyImageBorderRadius(img: fabric.Image, percent: number) {
  const w = Math.max(1, img.width || 1);
  const h = Math.max(1, img.height || 1);
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  img.set('borderRadius' as any, pct);

  if (pct <= 0) {
    img.set({ clipPath: undefined, dirty: true, objectCaching: true } as any);
    return;
  }

  const rx = (pct / 100) * (Math.min(w, h) / 2);
  const clip = new fabric.Rect({
    width: w,
    height: h,
    rx,
    ry: rx,
    originX: 'center',
    originY: 'center',
    absolutePositioned: false,
  });
  img.set({ clipPath: clip, dirty: true, objectCaching: false } as any);
}

function getObjectSceneBox(obj: fabric.Object): { left: number; top: number; width: number; height: number } {
  obj.setCoords();
  const aCoords = (obj as any).aCoords as
    | { tl?: { x: number; y: number }; tr?: { x: number; y: number }; br?: { x: number; y: number }; bl?: { x: number; y: number } }
    | undefined;
  if (aCoords?.tl && aCoords?.tr && aCoords?.br && aCoords?.bl) {
    const xs = [aCoords.tl.x, aCoords.tr.x, aCoords.br.x, aCoords.bl.x];
    const ys = [aCoords.tl.y, aCoords.tr.y, aCoords.br.y, aCoords.bl.y];
    const left = Math.min(...xs);
    const top = Math.min(...ys);
    return { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
  }
  const w = Math.max(1, obj.getScaledWidth());
  const h = Math.max(1, obj.getScaledHeight());
  const originX = String(obj.originX || 'left');
  const originY = String(obj.originY || 'top');
  let left = obj.left || 0;
  let top = obj.top || 0;
  if (originX === 'center') left -= w / 2;
  else if (originX === 'right') left -= w;
  if (originY === 'center') top -= h / 2;
  else if (originY === 'bottom') top -= h;
  return { left, top, width: w, height: h };
}

function isCanvasTextObject(obj: fabric.Object) {
  const t = String(obj.get('layerType') || obj.type || '');
  return t === 'text' || t === 'textbox' || t === 'i-text';
}

function getVisibleCanvasSceneBounds(
  canvas: fabric.Canvas | null | undefined,
  fallback: { width: number; height: number },
) {
  if (!canvas) {
    return { left: 0, top: 0, width: fallback.width, height: fallback.height };
  }
  const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
  const zoomX = Math.max(Math.abs(vpt[0] || canvas.getZoom() || 1), 0.0001);
  const zoomY = Math.max(Math.abs(vpt[3] || zoomX), 0.0001);
  return {
    left: (0 - vpt[4]) / zoomX,
    top: (0 - vpt[5]) / zoomY,
    width: canvas.getWidth() / zoomX,
    height: canvas.getHeight() / zoomY,
  };
}

function getFabricObjectControls() {
  const tryCreate = (Ctor: any) => {
    if (typeof Ctor?.createControls === 'function') {
      const created = Ctor.createControls();
      return created?.controls || null;
    }
    return Ctor?.prototype?.controls || null;
  };
  return (
    tryCreate((fabric as any).Image) ||
    tryCreate((fabric as any).Rect) ||
    tryCreate((fabric as any).FabricObject) ||
    tryCreate((fabric as any).Object)
  );
}

function applyTextImageLikeControls(obj: fabric.Object) {
  if (!isCanvasTextObject(obj)) return;
  const objectControls = getFabricObjectControls();
  if (objectControls) {
    (obj as any).controls = { ...objectControls };
  }
  obj.set({
    lockScalingFlip: true,
    lockUniScaling: false,
    centeredScaling: false,
    objectCaching: false,
    padding: 0,
    minScaleLimit: 0.05,
    hasControls: true,
    hasBorders: true,
  } as any);
  if (typeof obj.setControlsVisibility === 'function') {
    obj.setControlsVisibility({
      tl: true,
      tr: true,
      bl: true,
      br: true,
      ml: true,
      mr: true,
      mt: true,
      mb: true,
      mtr: true,
    });
  }
}

function constrainObjectToArtboard(
  obj: fabric.Object,
  logical: { width: number; height: number },
  canvas?: fabric.Canvas | null,
) {
  if (!obj || isIgnoredSelectionTarget(obj)) return;

  const isText = isCanvasTextObject(obj);
  const host = canvas || (obj.canvas as fabric.Canvas | null | undefined);
  const bounds = isText
    ? getVisibleCanvasSceneBounds(host, logical)
    : { left: 0, top: 0, width: Math.max(32, logical.width), height: Math.max(32, logical.height) };
  const maxW = Math.max(32, bounds.width);
  const maxH = Math.max(32, bounds.height);

  obj.setCoords();
  let box = getObjectSceneBox(obj);
  if (box.width > maxW || box.height > maxH) {
    const factor = Math.min(
      maxW / Math.max(box.width, 1),
      maxH / Math.max(box.height, 1),
      1,
    );
    obj.set({
      scaleX: Math.max(0.05, (obj.scaleX || 1) * factor),
      scaleY: Math.max(0.05, (obj.scaleY || 1) * factor),
    });
    obj.setCoords();
    box = getObjectSceneBox(obj);
  }

  // Text that is still larger than the canvas (hit min scale) must shrink locally.
  const isLiveTransform = !!(host && (host as any)._currentTransform);
  if (isText && !isLiveTransform && (box.width > maxW || box.height > maxH)) {
    const extra = Math.min(
      maxW / Math.max(box.width, 1),
      maxH / Math.max(box.height, 1),
      1,
    );
    const nextWidth = Math.max(32, (obj.width || 1) * extra);
    const nextFontSize = Math.max(10, (Number((obj as any).fontSize) || 12) * extra);
    obj.set({
      width: nextWidth,
      fontSize: nextFontSize,
    } as any);
    if (typeof (obj as any).initDimensions === 'function') {
      (obj as any).initDimensions();
    }
    obj.setCoords();
    box = getObjectSceneBox(obj);
  }

  let dx = 0;
  let dy = 0;
  if (box.left < bounds.left) dx = bounds.left - box.left;
  if (box.top < bounds.top) dy = bounds.top - box.top;
  if (box.left + box.width > bounds.left + maxW) {
    dx -= box.left + box.width - (bounds.left + maxW);
  }
  if (box.top + box.height > bounds.top + maxH) {
    dy -= box.top + box.height - (bounds.top + maxH);
  }
  if (dx || dy) {
    obj.set({
      left: (obj.left || 0) + dx,
      top: (obj.top || 0) + dy,
    });
    obj.setCoords();
  }
}

function constrainCropRectToImage(rect: fabric.Rect, img: fabric.Image) {
  const imgBox = getObjectSceneBox(img);
  const minSize = Math.max(48, Math.min(imgBox.width, imgBox.height) * 0.06);
  const rectBox = getObjectSceneBox(rect);

  let width = Math.max(minSize, rectBox.width);
  let height = Math.max(minSize, rectBox.height);
  let left = rectBox.left;
  let top = rectBox.top;

  if (width > imgBox.width) width = imgBox.width;
  if (height > imgBox.height) height = imgBox.height;
  if (left < imgBox.left) left = imgBox.left;
  if (top < imgBox.top) top = imgBox.top;
  if (left + width > imgBox.left + imgBox.width) left = imgBox.left + imgBox.width - width;
  if (top + height > imgBox.top + imgBox.height) top = imgBox.top + imgBox.height - height;

  rect.set({
    left,
    top,
    width,
    height,
    originX: 'left',
    originY: 'top',
    scaleX: 1,
    scaleY: 1,
    angle: 0,
  });
  rect.setCoords();
}

async function applyFabricCrop(
  canvas: fabric.Canvas,
  img: fabric.Image,
  cropRect: fabric.Rect,
) {
  const objectId = getObjectId(img);
  const layerName = img.get('layerName');
  const layerType = String(img.get('layerType') || 'image');
  const borderRadius = Number(img.get('borderRadius') || 0);
  img.set({ clipPath: undefined });
  const box = getObjectSceneBox(cropRect);
  const cropW = Math.max(1, box.width);
  const cropH = Math.max(1, box.height);
  const zoom = Math.max(canvas.getZoom(), 0.01);
  const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
  const vpLeft = box.left * vpt[0] + vpt[4];
  const vpTop = box.top * vpt[3] + vpt[5];

  cropRect.set({ visible: false });
  canvas.discardActiveObject();
  canvas.requestRenderAll();

  const dataUrl = canvas.toDataURL({
    format: 'png',
    quality: 1,
    left: vpLeft,
    top: vpTop,
    width: Math.max(1, cropW * zoom),
    height: Math.max(1, cropH * zoom),
    multiplier: 1 / zoom,
    filter: (obj: any) => {
      const t = String(obj.get('layerType') || '');
      const id = String(obj.get('object_id') || obj.get('layerId') || '');
      if (id === 'crop-rect' || t === 'crop' || t === 'background') return false;
      return obj === img;
    },
  } as any);

  await img.setSrc(dataUrl);

  img.set({
    cropX: 0,
    cropY: 0,
    originX: 'left',
    originY: 'top',
    left: box.left,
    top: box.top,
    scaleX: cropW / Math.max(1, img.width || 1),
    scaleY: cropH / Math.max(1, img.height || 1),
    layerType,
    layerName,
    dirty: true,
  } as any);
  if (objectId) stampObjectId(img, objectId);
  applyImageBorderRadius(img, borderRadius);
  img.setCoords();
  return true;
}

function isNotFoundError(err: any) {
  const status = err?.status || err?.originalStatus || err?.data?.code || err?.data?.status_code;
  const message = String(err?.data?.message || err?.message || '');
  return status === 404 || /not found/i.test(message);
}

function getItemDesignId(item: any) {
  return String(item?.design_id || item?.id || '').trim();
}

function parseDesignsList(res: any): any[] {
  const data = res?.data || res;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.designs)) return data.designs;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function safeDownloadName(name: string, ext: string) {
  let base = String(name || 'download');
  const lastDot = base.lastIndexOf('.');
  if (lastDot > 0) {
    base = base.substring(0, lastDot);
  }
  base = base.replace(/[^\w.-]/g, '_').split('_').filter(Boolean).join('_');
  return `${base || 'download'}.${ext}`;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const header = parts[0] || '';
  const data = parts[1] || '';
  let mime = 'application/octet-stream';
  const colonIdx = header.indexOf(':');
  const semiIdx = header.indexOf(';', colonIdx);
  if (colonIdx !== -1 && semiIdx > colonIdx) {
    mime = header.substring(colonIdx + 1, semiIdx);
  }
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function rasterToSvg(dataUrl: string, width: number, height: number) {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  return `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">\n  <image width="${w}" height="${h}" href="${dataUrl}" xlink:href="${dataUrl}"/>\n</svg>`;
}

const JPEG_EXPORT_BG = '#030A0C';
let suppressEditorGrid = false;

function collectExportSceneBounds(
  canvas: fabric.Canvas,
  logical: { width: number; height: number },
) {
  let minX = 0;
  let minY = 0;
  let maxX = logical.width;
  let maxY = logical.height;

  canvas.getObjects().forEach((obj) => {
    const t = String(obj.get('layerType') || '');
    if (t !== 'image' && t !== 'text' && t !== 'design') return;
    if (obj.visible === false) return;
    const box = getObjectSceneBox(obj);
    minX = Math.min(minX, box.left);
    minY = Math.min(minY, box.top);
    maxX = Math.max(maxX, box.left + box.width);
    maxY = Math.max(maxY, box.top + box.height);
  });

  return {
    left: Math.floor(minX),
    top: Math.floor(minY),
    width: Math.max(1, Math.ceil(maxX - minX)),
    height: Math.max(1, Math.ceil(maxY - minY)),
  };
}

function canvasElementToDataUrl(el: HTMLCanvasElement, format: 'png' | 'jpeg') {
  if (format === 'jpeg') {
    const out = document.createElement('canvas');
    out.width = el.width;
    out.height = el.height;
    const ctx = out.getContext('2d');
    if (!ctx) return el.toDataURL('image/png');
    ctx.fillStyle = JPEG_EXPORT_BG;
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(el, 0, 0);
    return out.toDataURL('image/jpeg', 0.92);
  }
  return el.toDataURL('image/png');
}

function rasterizeFabricCanvas(
  canvas: fabric.Canvas,
  format: 'png' | 'jpeg',
  logical: { width: number; height: number },
) {
  const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
  const zoomX = Math.max(Math.abs(vpt[0] || canvas.getZoom()), 0.01);
  const zoomY = Math.max(Math.abs(vpt[3] || zoomX), 0.01);
  const bounds = collectExportSceneBounds(canvas, logical);
  const multiplier = 1 / zoomX;

  suppressEditorGrid = true;
  try {
    const exportEl = canvas.toCanvasElement(multiplier, {
      left: vpt[4] + bounds.left * vpt[0],
      top: vpt[5] + bounds.top * vpt[3],
      width: Math.max(1, bounds.width * zoomX),
      height: Math.max(1, bounds.height * zoomY),
      filter: (obj: fabric.Object) => {
        const t = String(obj.get('layerType') || '');
        const id = String(obj.get('object_id') || obj.get('layerId') || '');
        if (id === 'crop-rect' || t === 'crop' || t === 'background') return false;
        return obj.visible !== false;
      },
    } as any);
    return canvasElementToDataUrl(exportEl, format);
  } finally {
    suppressEditorGrid = false;
    canvas.requestRenderAll();
  }
}

let designEntranceRaf = 0;

function playGeneratedDesignEntrance(
  canvas: fabric.Canvas,
  img: fabric.Image,
  logical: { width: number; height: number },
) {
  if (designEntranceRaf) cancelAnimationFrame(designEntranceRaf);

  const endScaleX = img.scaleX || 1;
  const endScaleY = img.scaleY || 1;
  const startFactor = 0.9;
  const startScaleX = endScaleX * startFactor;
  const startScaleY = endScaleY * startFactor;
  const startLeft = (logical.width * (1 - startFactor)) / 2;
  const startTop = (logical.height * (1 - startFactor)) / 2;

  img.set({
    opacity: 0,
    scaleX: startScaleX,
    scaleY: startScaleY,
    left: startLeft,
    top: startTop,
  });
  canvas.requestRenderAll();

  const duration = 780;
  const t0 = performance.now();
  const tick = (now: number) => {
    const t = Math.min(1, (now - t0) / duration);
    const eased = 1 - (1 - t) ** 3;
    img.set({
      opacity: eased,
      scaleX: startScaleX + (endScaleX - startScaleX) * eased,
      scaleY: startScaleY + (endScaleY - startScaleY) * eased,
      left: startLeft * (1 - eased),
      top: startTop * (1 - eased),
    });
    canvas.requestRenderAll();
    if (t < 1) {
      designEntranceRaf = requestAnimationFrame(tick);
      return;
    }
    designEntranceRaf = 0;
    img.set({
      opacity: 1,
      scaleX: endScaleX,
      scaleY: endScaleY,
      left: 0,
      top: 0,
    });
    img.setCoords();
    canvas.requestRenderAll();
  };
  designEntranceRaf = requestAnimationFrame(tick);
}

const Dashboard: React.FC<DashboardProps> = ({ activeOverlay }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const [activeTool, setActiveTool] = useState<ToolType>('cursor');
  const activeToolRef = useRef<ToolType>('cursor');
  activeToolRef.current = activeTool;
  const [isGridVisible, setIsGridVisible] = useState(false);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const zoomPercentRef = useRef(100);
  const zoomMenuRef = useRef<HTMLDivElement>(null);
  zoomPercentRef.current = zoomPercent;
  const [cropState, setCropState] = useState<{ active: boolean; originalObj: fabric.Image | null; cropRect: fabric.Rect | null; objectId: string | null }>({ active: false, originalObj: null, cropRect: null, objectId: null });
  const cropRectRef = useRef<fabric.Rect | null>(null);
  const cropTargetRef = useRef<fabric.Image | null>(null);
  const cropTargetObjectIdRef = useRef<string | null>(null);
  const applyingCropRef = useRef(false);
  const regionMaskCacheRef = useRef<Record<string, HTMLCanvasElement>>({});
  const allDesignRegionsRef = useRef<RegionData[]>([]);
  const originalPreviewUrlRef = useRef<string | null>(null);
  const [maskCacheVersion, setMaskCacheVersion] = useState(0);
  const [isCanvasReady, setIsCanvasReady] = useState(false);


  const containerRef = useRef<HTMLDivElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const logicalSizeRef = useRef({ ...DEFAULT_LOGICAL_SIZE });
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeLayerIdRef = useRef<string | null>(null);
  const suppressSelectionClearedRef = useRef(false);
  const dispatch = useDispatch();
  const { regions, regionColors, currentDesign, activeLayerId, layers, isGenerating, isRestoringDesign, isSavingDesign } = useSelector((state: RootState) => state.editor);
  activeLayerIdRef.current = activeLayerId;
  const currentDesignRef = useRef(currentDesign);
  const regionsRef = useRef(regions);
  const regionColorsRef = useRef(regionColors);
  const layersRef = useRef(layers);
  currentDesignRef.current = currentDesign;
  regionsRef.current = regions;
  regionColorsRef.current = regionColors;
  layersRef.current = layers;

  const isRestoringHistoryRef = useRef(false);
  const historyPastRef = useRef<EditorHistorySnapshot[]>([]);
  const historyPresentRef = useRef<EditorHistorySnapshot | null>(null);
  const historyFutureRef = useRef<EditorHistorySnapshot[]>([]);
  const historyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRecolorKeyRef = useRef('');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncHistoryButtons = () => {
    const canUndoNow = historyPastRef.current.length > 0;
    const canRedoNow = historyFutureRef.current.length > 0;
    setCanUndo(canUndoNow);
    setCanRedo(canRedoNow);
    window.dispatchEvent(new CustomEvent('editor-history-changed', {
      detail: {
        canUndo: canUndoNow,
        canRedo: canRedoNow,
      },
    }));
  };

  const snapshotNow = useCallback((): EditorHistorySnapshot | null => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;
    return takeEditorSnapshot(canvas, layersRef.current, regionColorsRef.current, regionsRef.current);
  }, []);

  const resetHistoryBaseline = useCallback(() => {
    if (historyTimerRef.current) {
      clearTimeout(historyTimerRef.current);
      historyTimerRef.current = null;
    }
    historyPastRef.current = [];
    historyFutureRef.current = [];
    historyPresentRef.current = snapshotNow();
    lastRecolorKeyRef.current = JSON.stringify(regionColorsRef.current || {});
    syncHistoryButtons();
  }, [snapshotNow]);

  const commitHistory = useCallback(() => {
    if (isRestoringHistoryRef.current) return;
    const next = snapshotNow();
    if (!next) return;
    if (!historyPresentRef.current) {
      historyPresentRef.current = next;
      return;
    }
    historyPastRef.current.push(historyPresentRef.current);
    if (historyPastRef.current.length > 40) historyPastRef.current.shift();
    historyPresentRef.current = next;
    historyFutureRef.current = [];
    syncHistoryButtons();
  }, [snapshotNow]);

  const commitHistoryDebounced = useCallback(() => {
    if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    historyTimerRef.current = setTimeout(() => {
      historyTimerRef.current = null;
      commitHistory();
    }, 350);
  }, [commitHistory]);

  const commitHistoryRef = useRef(commitHistory);
  const commitHistoryDebouncedRef = useRef(commitHistoryDebounced);
  const resetHistoryBaselineRef = useRef(resetHistoryBaseline);
  commitHistoryRef.current = commitHistory;
  commitHistoryDebouncedRef.current = commitHistoryDebounced;
  resetHistoryBaselineRef.current = resetHistoryBaseline;

  const restoreHistorySnapshot = useCallback(async (snap: EditorHistorySnapshot) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    isRestoringHistoryRef.current = true;
    lastRecolorKeyRef.current = JSON.stringify(snap.regionColors || {});
    const width = canvas.getWidth();
    const height = canvas.getHeight();
    const vpt = [...(canvas.viewportTransform || [1, 0, 0, 1, 0, 0])] as number[];
    try {
      await canvas.loadFromJSON(snap.json);
      canvas.setDimensions({ width, height });
      canvas.setViewportTransform(vpt as any);
      canvas.getObjects().forEach((obj) => {
        applyObjectToolState(obj, activeToolRef.current);
        bindTextEditingGuards(obj);
        if (isUploadImageLayer(obj)) {
          applyImageBorderRadius(obj, Number(obj.get('borderRadius') || 0));
        }
      });
      restackEditorLayers(canvas, snap.layers.map((layer) => layer.layerId));
      if (snap.regions) {
        dispatch(setDesignRegions(snap.regions));
      }
      dispatch(setLayers(snap.layers as any));
      Object.entries(snap.regionColors || {}).forEach(([id, color]) => {
        dispatch(setRegionColor({ id, color }));
      });
      canvas.discardActiveObject();
      applyViewportFit(canvas);
      canvas.getObjects().forEach((obj) => {
        if (isCanvasTextObject(obj)) {
          constrainObjectToArtboard(obj, logicalSizeRef.current, canvas);
        }
      });
      canvas.requestRenderAll();
    } catch (err) {
      console.error('[History] restore failed', err);
      toast.error('Could not undo/redo that change.');
    } finally {
      isRestoringHistoryRef.current = false;
    }
  }, [dispatch]);

  const handleUndo = useCallback(async () => {
    if (isRestoringHistoryRef.current || !historyPastRef.current.length) return;
    const current = snapshotNow();
    const previous = historyPastRef.current.pop();
    if (!previous) return;
    if (current) historyFutureRef.current.push(current);
    historyPresentRef.current = previous;
    syncHistoryButtons();
    await restoreHistorySnapshot(previous);
  }, [restoreHistorySnapshot, snapshotNow]);

  const handleRedo = useCallback(async () => {
    if (isRestoringHistoryRef.current || !historyFutureRef.current.length) return;
    const current = snapshotNow();
    const next = historyFutureRef.current.pop();
    if (!next) return;
    if (current) historyPastRef.current.push(current);
    historyPresentRef.current = next;
    syncHistoryButtons();
    await restoreHistorySnapshot(next);
  }, [restoreHistorySnapshot, snapshotNow]);

  const { data: designsRes } = useGetDesignsQuery({ limit: 6, sort_by: 'newest' });
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const urlDesignId = queryParams.get('design_id');

  const [getDesign, { reset: resetGetDesign }] = useLazyGetDesignQuery();
  const [updateDesign, { isLoading: isUpdatingDesign }] = useUpdateDesignMutation();
  const [uploadMediaApi] = useUploadMediaMutation();
  const [exportDesignApi] = useExportDesignMutation();
  const [getExportStatus] = useLazyGetDesignExportQuery();
  const [saveDesignFinalApi, { isLoading: isSavingFinal }] = useSaveDesignFinalMutation();
  const [deleteDesign] = useDeleteDesignMutation();
  const [deletingDesignId, setDeletingDesignId] = useState<string | null>(null);
  const removedRecentIdsRef = useRef<Set<string>>(new Set());
  const [removedRecentIds, setRemovedRecentIds] = useState<string[]>([]);
  const recentDesigns = parseDesignsList(designsRes)
    .filter((item: any) => !removedRecentIdsRef.current.has(getItemDesignId(item)))
    .slice(0, 6);

  const applyViewportFit = (canvas?: fabric.Canvas | null) => {
    const c = canvas || fabricCanvasRef.current;
    const workspace = containerRef.current;
    if (!c || !workspace) return;

    const { width: lw, height: lh } = logicalSizeRef.current;
    const rect = workspace.getBoundingClientRect();
    const availW = Math.max(120, Math.round(rect.width));
    const availH = Math.max(120, Math.round(rect.height));
    const baseZoom = Math.min(availW / lw, availH / lh);
    const zoom = baseZoom * (Math.max(25, zoomPercentRef.current) / 100);
    const offsetX = (availW - lw * zoom) / 2;
    const offsetY = (availH - lh * zoom) / 2;

    const sameSize = c.getWidth() === availW && c.getHeight() === availH;
    const sameZoom = Math.abs(c.getZoom() - zoom) < 0.0001;
    if (sameSize && sameZoom) return;

    c.setDimensions({ width: availW, height: availH });
    c.setZoom(zoom);
    const vpt = c.viewportTransform;
    if (vpt) {
      vpt[4] = offsetX;
      vpt[5] = offsetY;
      c.setViewportTransform(vpt);
    }
    c.requestRenderAll();
  };

  const serializeOverlayLayers = (canvas: fabric.Canvas) => {
    const isOverlayObj = (obj: fabric.Object) => {
      const t = String(obj.get('layerType') || '');
      return t === 'image' || t === 'text' || t === 'design';
    };

    const applyTextProps = (layer: any, obj: fabric.Object, layerType: string) => {
      if (layerType === 'text' || obj.type === 'textbox' || obj.type === 'i-text') {
        const textObj = obj as any;
        layer.text = textObj.text;
        layer.fill = textObj.fill;
        layer.fontFamily = textObj.fontFamily;
        layer.fontSize = textObj.fontSize;
        layer.textAlign = textObj.textAlign;
      }
    };

    const applyMediaProps = (layer: any, obj: fabric.Object) => {
      const mediaUrl = String(obj.get('mediaFileUrl') || '');
      const mediaId = obj.get('mediaId');
      if (mediaId != null) {
        layer.media_id = mediaId;
        layer.asset_id = mediaId;
        layer.file_id = mediaId;
      }
      if (mediaUrl) {
        layer.src = mediaUrl;
        layer.media_file_url = mediaUrl;
        layer.url = mediaUrl;
        layer.image_url = mediaUrl;
        layer.source_url = mediaUrl;
      } else if (typeof (obj as fabric.Image).getSrc === 'function') {
        const src = (obj as fabric.Image).getSrc();
        if (src && !src.startsWith('blob:') && !src.startsWith('data:')) {
          layer.src = src;
          layer.media_file_url = src;
          layer.url = src;
          layer.image_url = src;
          layer.source_url = src;
        }
      }
    };

    const mapOverlayLayer = (obj: fabric.Object, index: number) => {
      const layerType = String(obj.get('layerType') || obj.type);
      const parsedType = layerType === 'text' ? 'text' : (layerType === 'design' ? 'design' : 'image');
      const lid = obj.get('layerId');
      const isNumeric = lid && !isNaN(Number(lid));

      const layer: any = {
        id: isNumeric ? Number(lid) : undefined,
        object_id: lid || `layer-${index}`,
        type: parsedType,
        z_index: index + 10,
        visible: obj.visible !== false,
        locked: obj.selectable === false,
        opacity: obj.opacity ?? 1,
        width: obj.width ?? null,
        height: obj.height ?? null,
        transform: {
          left: obj.left || 0,
          top: obj.top || 0,
          scale_x: obj.scaleX || 1,
          scale_y: obj.scaleY || 1,
          angle: obj.angle || 0,
        },
      };

      applyTextProps(layer, obj, layerType);
      applyMediaProps(layer, obj);

      if (layer.type === 'image') {
        layer.border_radius = Number(obj.get('borderRadius') || 0);
      }
      layer.blend_mode = String((obj as any).globalCompositeOperation || 'source-over');
      return layer;
    };

    return canvas.getObjects().filter(isOverlayObj).map(mapOverlayLayer);
  };

  const validateDesignForSave = (canvas: fabric.Canvas, silent?: boolean) => {
    const invalidTextObj = canvas.getObjects().find((obj) => (
      isCanvasTextObject(obj) && !!getTextContentError((obj as any).text)
    ));
    if (invalidTextObj) {
      const error = getTextContentError((invalidTextObj as any).text);
      window.dispatchEvent(new CustomEvent('editor-text-validation', {
        detail: { layerId: getObjectId(invalidTextObj), error },
      }));
      if (!silent) toast.error(error, { toastId: 'canvas-text-empty' });
      return false;
    }
    return true;
  };

  const uploadPreviewToS3 = async (blob: Blob, designId: string): Promise<string | null> => {
    try {
      const fileName = `preview_${designId}_${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      const formData = new FormData();
      formData.append('media_file', file);
      formData.append('file_type', 'img');

      const response = await uploadMediaApi(formData).unwrap();
      const url = response?.data?.media_file_url || response?.media_file_url;
      if (url) return url;
    } catch (err) {
      console.warn('[UploadPreview] uploadMediaApi failed:', err);
    }
    return null;
  };

  const buildSavePayload = (design: any, canvas: fabric.Canvas, status?: string, newPreviewUrl?: string) => {
    const activeLayerIds = new Set([
      ...layersRef.current.map((l) => l.layerId),
      ...regionsRef.current.map((r) => r.id),
    ]);
    const isBaseLayer = (layer: any) => {
      const type = layer?.type;
      if (type === 'image' || type === 'text' || type === 'textbox' || type === 'design') return false;
      const lid = String(layer?.object_id || layer?.id || '');
      return !lid || activeLayerIds.has(lid);
    };
    const baseLayers = Array.isArray(design.layers) ? design.layers.filter(isBaseLayer) : [];

    const overlayLayers = serializeOverlayLayers(canvas);
    const { width, height } = logicalSizeRef.current;

    const isCustomizedLayer = (l: any) => l.type === 'image' || l.type === 'text';
    const isCustomized = overlayLayers.some(isCustomizedLayer);

    const mapRegion = (region: any) => ({
      region_id: region.id,
      name: region.name,
      original_color: region.originalColor,
      color: regionColorsRef.current[region.id] || (region as any).currentColor || region.originalColor,
      current_color: regionColorsRef.current[region.id] || (region as any).currentColor || region.originalColor,
      mask_url: region.maskUrl,
      bounds: region.bounds,
    });

    const normalizeStatus = (s?: string) => {
      if (s === 'Saved' || s === 'Claimed') return s;
      return 'Draft';
    };

    return {
      design_id: design.design_id,
      title: design.title || 'Regenerated Design',
      status: normalizeStatus(status || design.status),
      seed: design.seed || null,
      version: (design.version || 1) + 1,
      canvas: { width, height },
      layers: [...baseLayers, ...overlayLayers],
      regions: regionsRef.current.map(mapRegion),
      ...(newPreviewUrl ? { preview_url: newPreviewUrl } : {})
    };
  };

  const isSavingRef = useRef(false);

  const saveCurrentDesign = async (status?: string, { silent }: { silent?: boolean } = {}) => {
    if (isSavingRef.current) return false;

    const canvas = fabricCanvasRef.current;
    const design = currentDesignRef.current;
    if (!canvas || !design?.design_id) {
      if (!silent) toast.error('Generate a design before saving.');
      return;
    }
    if (removedRecentIdsRef.current.has(String(design.design_id))) return;
    if (!validateDesignForSave(canvas, silent)) return false;

    isSavingRef.current = true;
    dispatch(setIsSavingDesign(true));
    try {
      console.log("[Apply Overlay] Original Design Object:", design);
      console.log("[Apply Overlay] Sending to API - Design ID:", design.design_id);

      const prevActive = canvas.getActiveObject();
      canvas.discardActiveObject();

      const hiddenCrop = canvas.getObjects().filter((obj) => String(obj.get('layerType') || '') === 'crop' || getObjectId(obj) === 'crop-rect');
      hiddenCrop.forEach((obj) => obj.set('visible', false));

      const vpt = canvas.viewportTransform ? [...canvas.viewportTransform] : [1, 0, 0, 1, 0, 0];
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.renderAll();

      const logical = logicalSizeRef.current;
      const previewDataUrl = canvas.toDataURL({
        format: 'png',
        quality: 1,
        left: 0,
        top: 0,
        width: logical.width,
        height: logical.height,
        filter: (obj: fabric.Object) => {
          const t = String(obj.get('layerType') || '');
          const id = String(obj.get('object_id') || obj.get('layerId') || '');
          if (id === 'crop-rect' || t === 'crop' || t === 'background') return false;
          return obj.visible !== false;
        },
      } as any);

      canvas.setViewportTransform(vpt as any);
      hiddenCrop.forEach((obj) => obj.set('visible', true));
      if (prevActive && canvas.getObjects().includes(prevActive)) {
        canvas.setActiveObject(prevActive);
      }
      canvas.renderAll();

      const previewBlob = dataUrlToBlob(previewDataUrl);
      const newPreviewUrl = await uploadPreviewToS3(previewBlob, design.design_id);

      const body = buildSavePayload(design, canvas, status, newPreviewUrl || undefined);

      console.log("[Apply Overlay] PUT Payload:", JSON.stringify(body, null, 2));

      try {
        const res = await updateDesign({ designId: design.design_id, body }).unwrap();
        const saved = res?.data || res;
        dispatch(setCurrentDesign({
          ...design,
          ...saved,
          preview_url: newPreviewUrl || saved?.preview_url || design?.preview_url,
          layers: body.layers,
          regions: body.regions,
        }));
        if (!silent) toast.success('Design saved.');
        return true;
      } catch (err: any) {
        const errStatus = err?.status || err?.originalStatus || err?.data?.status_code || err?.data?.code;
        const message = String(err?.data?.message || err?.message || 'Failed to save design');
        const missing = errStatus === 404 || /not found/i.test(message);
        if (missing) {
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
          currentDesignRef.current = null;
          dispatch(setCurrentDesign(null));
          resetGetDesign();
          return false;
        }
        if (!silent) toast.error(message, { toastId: `design-save-${design.design_id}` });
        console.error('[Save] PUT failed:', err);
        return false;
      }
    } finally {
      isSavingRef.current = false;
      dispatch(setIsSavingDesign(false));
    }
  };

  const queueAutosave = () => {
    if (!currentDesignRef.current?.design_id) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveCurrentDesign('Draft', { silent: true });
    }, 2500);
  };

  const openRecentDesign = async (item: any) => {
    if (!item?.design_id) return;
    if (isRestoringDesign) return;
    dispatch(setIsRestoringDesign(true));

    if (String(item.design_id).startsWith('static-')) {
      const data = {
        design_id: item.design_id,
        preview_url: item.preview_url,
        canvas: { width: 2048, height: 2048 },
        layers: [],
        regions: [],
      };
      dispatch(setCurrentDesign(data));
      window.dispatchEvent(new CustomEvent('update-main-design', {
        detail: {
          designData: data,
          design_id: data.design_id,
          preview_url: data.preview_url,
          canvas: data.canvas,
          layers: data.layers,
          regions: data.regions,
          isRestore: true,
        }
      }));
      return;
    }

    try {
      const res = await getDesign(item.design_id).unwrap();
      const data = res?.data || res;
      dispatch(setCurrentDesign(data));
      window.dispatchEvent(new CustomEvent('update-main-design', {
        detail: {
          designData: data,
          design_id: data.design_id,
          preview_url: data.preview_url,
          canvas: data.canvas,
          layers: data.layers,
          regions: data.regions,
          isRestore: true,
        }
      }));
    } catch (err: any) {
      const message = err?.data?.message || 'Failed to open design';
      toast.error(message, { toastId: `design-open-${item.design_id}` });
      dispatch(setIsRestoringDesign(false));
    }
  };

  const handleDeleteRecent = async (e: React.MouseEvent, item: any) => {
    e.preventDefault();
    e.stopPropagation();
    const designId = getItemDesignId(item);
    if (!designId || designId.startsWith('static-')) return;
    try {
      setDeletingDesignId(designId);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      resetGetDesign();
      if (String(currentDesignRef.current?.design_id || '') === designId) {
        currentDesignRef.current = null;
        dispatch(setCurrentDesign(null));
        dispatch(clearRegions());
      }
      removedRecentIdsRef.current.add(designId);
      setRemovedRecentIds(Array.from(removedRecentIdsRef.current));
      try {
        await deleteDesign(designId).unwrap();
      } catch (err: any) {
        if (!isNotFoundError(err)) throw err;
      }
      toast.success('Design deleted.');
    } catch (err: any) {
      removedRecentIdsRef.current.delete(designId);
      setRemovedRecentIds(Array.from(removedRecentIdsRef.current));
      toast.error(err?.data?.message || err?.message || 'Failed to delete design', { toastId: `design-delete-${designId}` });
    } finally {
      setDeletingDesignId(null);
    }
  };

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isCanvasReady) return;
    resetHistoryBaselineRef.current();

    if (currentDesignRef.current && currentDesignRef.current.design_id) {
      const canvas = fabricCanvasRef.current;
      const hasDesign = canvas?.getObjects().some(obj => obj.get('layerType') === 'design');
      if (!hasDesign) {
        dispatch(setIsRestoringDesign(true));
        window.dispatchEvent(new CustomEvent('update-main-design', {
          detail: {
            ...currentDesignRef.current,
            designData: currentDesignRef.current,
            isRestore: true
          }
        }));
      }
    }
  }, [isCanvasReady]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const canvas = fabricCanvasRef.current;
      const activeObject = canvas?.getActiveObject() as any;
      if (activeObject?.isEditing) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        handleRedo();
      }
    };
    const emitHistoryState = () => {
      window.dispatchEvent(new CustomEvent('editor-history-changed', {
        detail: {
          canUndo: historyPastRef.current.length > 0,
          canRedo: historyFutureRef.current.length > 0,
        },
      }));
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('editor-undo', handleUndo);
    window.addEventListener('editor-redo', handleRedo);
    window.addEventListener('editor-history-request', emitHistoryState);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('editor-undo', handleUndo);
      window.removeEventListener('editor-redo', handleRedo);
      window.removeEventListener('editor-history-request', emitHistoryState);
    };
  }, [handleUndo, handleRedo]);

  // Recolor the single preview image in place. Masks and tint copies never go on the canvas.
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    let cancelled = false;

    const syncDesignColor = async () => {
      canvas.getObjects().filter(isRegionCanvasLayer).forEach((obj) => canvas.remove(obj));

      const designImg = canvas.getObjects().find((obj) => obj.get('layerType') === 'design') as fabric.Image | undefined;
      const originalUrl = originalPreviewUrlRef.current;
      if (!designImg || !originalUrl) {
        canvas.requestRenderAll();
        return;
      }

      const logical = logicalSizeRef.current;
      const jobs = (regions || [])
        .map((region) => {
          const currentColor = regionColors[region.id] || (region as any).currentColor || (region as any).color;
          const baseColor = region.originalColor || '';
          const changed = !!currentColor && currentColor.toLowerCase() !== baseColor.toLowerCase();
          const mask = regionMaskCacheRef.current[region.id];
          if (!changed || !mask) return null;
          return { mask, color: currentColor };
        })
        .filter((job): job is { mask: HTMLCanvasElement; color: string } => !!job);

      const activeRegions = regions || [];
      const hiddenOrDeletedRegions = allDesignRegionsRef.current.filter((r) => (
        !activeRegions.some((ar) => ar.id === r.id) ||
        layersRef.current.some((l) => l.layerId === r.id && l.visible === false)
      ));
      const deletedMasks = hiddenOrDeletedRegions
        .map((r) => regionMaskCacheRef.current[r.id])
        .filter((mask): mask is HTMLCanvasElement => !!mask);

      try {
        const source = await loadHtmlImage(originalUrl);
        if (cancelled) return;
        const dataUrl = (jobs.length === 0 && deletedMasks.length === 0)
          ? originalUrl
          : applyRegionRecolors(source, logical, jobs, deletedMasks);
        if (!dataUrl || cancelled) return;

        await designImg.setSrc(dataUrl);
        if (cancelled) return;
        designImg.set({ dirty: true } as any);
        applyObjectToolState(designImg, activeToolRef.current);
        designImg.setCoords();
        restackEditorLayers(canvas, layersRef.current.map((layer) => layer.layerId));
        canvas.requestRenderAll();
        const colorKey = JSON.stringify({
          colors: regionColorsRef.current || {},
          regions: (regions || []).map((r) => r.id),
          layers: (layersRef.current || []).map((l) => ({ id: l.layerId, v: l.visible !== false })),
        });
        if (lastRecolorKeyRef.current && lastRecolorKeyRef.current !== colorKey) {
          commitHistoryDebouncedRef.current();
        }
        lastRecolorKeyRef.current = colorKey;
      } catch (err) {
        console.warn('[RegionColor] in-place recolor failed', err);
      }
    };

    const timer = window.setTimeout(() => {
      syncDesignColor();
    }, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [regions, regionColors, maskCacheVersion, layers]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (fabricCanvasRef.current) return;

    let canvas: fabric.Canvas | null = null;

    const updateReduxProps = (obj: fabric.Object) => {
      if (!obj || obj.get('layerType') === 'background') return;
      dispatch(setSelectedObjectProps(collectObjectProps(obj)));
      if (isCanvasTextObject(obj)) {
        const text = String((obj as any).text || '');
        const id = getObjectId(obj);
        if (text && id) {
          obj.set('layerName', text);
          dispatch(updateLayer({ layerId: id, layerName: text }));
        }
      }
    };

    const handleSelection = (e: any) => {
      const obj = e.selected?.[0];
      if (!obj || isIgnoredSelectionTarget(obj)) return;
      const objectId = getObjectId(obj);
      console.log('[LayerSelect]', { objectId, source: 'canvas' });
      console.log('[FabricSelection]', { objectId });
      commitActiveLayer(dispatch, activeLayerIdRef, objectId);
      if (isCanvasTextObject(obj)) applyTextImageLikeControls(obj);
      constrainObjectToArtboard(obj, logicalSizeRef.current, fabricCanvasRef.current!);
      updateReduxProps(obj);
    };

    const handleSelectionCleared = () => {
      if (suppressSelectionClearedRef.current) return;
      commitActiveLayer(dispatch, activeLayerIdRef, null);
      dispatch(setSelectedObjectProps(null));
    };

    const handleObjectModified = (e: any) => {
      if (e.target) {
        if (isCanvasTextObject(e.target)) {
          const error = getTextContentError((e.target as any).text);
          if (error) {
            restoreBlankCanvasText(e.target);
            toast.error(error, { toastId: 'canvas-text-empty' });
          } else {
            rememberValidCanvasText(e.target);
          }
        }
        constrainObjectToArtboard(e.target, logicalSizeRef.current, fabricCanvasRef.current!);
        updateReduxProps(e.target);
      }
      if (!isRestoringHistoryRef.current && e.target && !isIgnoredSelectionTarget(e.target)) {
        commitHistoryRef.current();
      }
    };

    const handleObjectAdded = (e: any) => {
      const target = e.target;
      if (!target) return;
      bindTextEditingGuards(target);
      if (isRestoringHistoryRef.current || isIgnoredSelectionTarget(target)) return;
      commitHistoryRef.current();
    };

    const handleObjectRemoved = (e: any) => {
      const target = e.target;
      if (!target || isRestoringHistoryRef.current || isIgnoredSelectionTarget(target)) return;
      commitHistoryRef.current();
    };

    const handleObjectMoving = (e: any) => {
      const target = e.target;
      if (!target) return;
      if (target.get('layerType') === 'background') {
        target.set({ left: 0, top: 0, angle: 0 });
        target.setCoords();
        return;
      }
      constrainObjectToArtboard(target, logicalSizeRef.current, fabricCanvasRef.current!);
      updateReduxProps(target);
    };

    const handleObjectScalingResizing = (e: any) => {
      const target = e.target;
      if (!target || target.get('layerType') === 'background') return;
      constrainObjectToArtboard(target, logicalSizeRef.current, fabricCanvasRef.current!);
      updateReduxProps(target);
    };

    const handleObjectRotating = (e: any) => {
      const target = e.target;
      if (!target) return;
      if (target.get('layerType') === 'background') {
        target.set({ angle: 0 });
        target.setCoords();
        return;
      }
      constrainObjectToArtboard(target, logicalSizeRef.current, fabricCanvasRef.current!);
      updateReduxProps(target);
    };

    const initCanvas = () => {
      const canvasEl = document.createElement('canvas');
      if (!containerRef.current) return;
      containerRef.current.appendChild(canvasEl);

      const canvas = new fabric.Canvas(canvasEl, {
        width: 600,
        height: 600,
        selection: true,
        interactive: true,
        preserveObjectStacking: true
      });

      fabricCanvasRef.current = canvas;
      logicalSizeRef.current = { ...DEFAULT_LOGICAL_SIZE };
      applyViewportFit(canvas);

      // No solid white background to allow the mock canvas graphic to show through
      const bg = new fabric.Rect({
        left: 0,
        top: 0,
        width: logicalSizeRef.current.width,
        height: logicalSizeRef.current.height,
        fill: 'transparent',
        selectable: false,
        evented: false,
      });
      bg.set('layerType', 'background');
      bg.set('layerName', 'Background');
      stampObjectId(bg, 'canvas-background');
      canvas.add(bg);

      // Notify that canvas is ready for the activeIndex useEffect
      setIsCanvasReady(true);

      canvas.on('selection:created', handleSelection);
      canvas.on('selection:updated', handleSelection);
      canvas.on('selection:cleared', handleSelectionCleared);
      canvas.on('object:modified', handleObjectModified);
      canvas.on('object:added', handleObjectAdded);
      canvas.on('object:removed', handleObjectRemoved);
      canvas.on('object:moving', handleObjectMoving);
      canvas.on('object:scaling', handleObjectScalingResizing);
      canvas.on('object:resizing', handleObjectScalingResizing);
      canvas.on('object:rotating', handleObjectRotating);
    };

    // Delay initialization to ensure DOM is fully laid out
    const timeoutId = setTimeout(initCanvas, 100);

    const handleAddImage = async (e: Event) => {
      const customEvent = e as CustomEvent;
      const { url, layerId, layerName, mediaId, mediaFileUrl } = customEvent.detail;
      const canvas = fabricCanvasRef.current;
      if (!canvas || !url) return;

      try {
        const img = await loadFabricImage(url);
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch {
            // ignore
          }
        }
        const { width: lw, height: lh } = logicalSizeRef.current;
        const imgW = img.width || 1;
        const imgH = img.height || 1;
        const target = Math.min(lw, lh) * 0.45;
        const scale = Math.min(target / imgW, target / imgH, 1);

        const finalLeft = (lw - imgW * scale) / 2;
        const finalTop = (lh - imgH * scale) / 2;

        img.set({
          layerId,
          object_id: layerId,
          layerType: 'image',
          layerName,
          mediaId,
          mediaFileUrl: mediaFileUrl || url,
          borderRadius: 0,
          originX: 'left',
          originY: 'top',
          left: finalLeft,
          top: finalTop,
          scaleX: scale,
          scaleY: scale,
          hasControls: true,
          hasBorders: true,
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
        });
        stampObjectId(img, layerId);
        applyObjectToolState(img, activeToolRef.current);
        img.setCoords();
        canvas.add(img);
        restackEditorLayers(canvas, layersRef.current.map((layer) => layer.layerId));
        if (activeToolRef.current === 'cursor') {
          activateCanvasLayer(canvas, layerId, 'layers-panel', {
            dispatch,
            activeLayerIdRef,
            suppressSelectionClearedRef,
          });
        } else {
          commitActiveLayer(dispatch, activeLayerIdRef, layerId);
        }
        canvas.requestRenderAll();
        updateReduxProps(img);
      } catch (err) {
        console.error("Fabric Image load error:", err);
        toast.error("Failed to add uploaded image to the canvas.");
      }
    };
    window.addEventListener('add-fabric-image', handleAddImage);

    const fitCompositionToCanvas = (canvas: fabric.Canvas) => {
      const objects = canvas.getObjects().filter(obj => obj.get('layerType') !== 'background' && obj.get('layerId') !== 'crop-rect');
      if (objects.length === 0) return;

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      objects.forEach(obj => {
        const br = obj.getBoundingRect();
        minX = Math.min(minX, br.left);
        minY = Math.min(minY, br.top);
        maxX = Math.max(maxX, br.left + br.width);
        maxY = Math.max(maxY, br.top + br.height);
      });

      const compWidth = maxX - minX;
      const compHeight = maxY - minY;
      if (compWidth === 0 || compHeight === 0) return;

      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();
      const padding = Math.min(canvasWidth, canvasHeight) * 0.1;
      const availableWidth = canvasWidth - (padding * 2);
      const availableHeight = canvasHeight - (padding * 2);

      const scale = Math.min(availableWidth / compWidth, availableHeight / compHeight);

      const compCenterX = minX + (compWidth / 2);
      const compCenterY = minY + (compHeight / 2);
      const targetCenterX = canvasWidth / 2;
      const targetCenterY = canvasHeight / 2;

      objects.forEach(obj => {
        const currentLeft = obj.left || 0;
        const currentTop = obj.top || 0;
        obj.set({
          scaleX: (obj.scaleX || 1) * scale,
          scaleY: (obj.scaleY || 1) * scale,
          left: targetCenterX + (currentLeft - compCenterX) * scale,
          top: targetCenterY + (currentTop - compCenterY) * scale
        });
        obj.setCoords();
      });

      canvas.requestRenderAll();
    };

    let designLoadSeq = 0;
    const handleUpdateMainDesign = async (e: Event) => {
      const customEvent = e as CustomEvent;
      const payload = customEvent.detail || {};
      const shouldAnimate = !!payload.animate;
      const announceSuccess = !!payload.announceSuccess;
      const seq = ++designLoadSeq;
      console.log('[Canvas] update-main-design received:', payload);

      const designData = payload.designData || payload;
      const assetData = payload.assetData || {};
      const getExpUrl = (e: any) => typeof e === 'object' && e ? (e.png || e.jpg || e.svg) : e;
      const previewUrl = payload.preview_url
        || designData?.preview_url
        || getExpUrl(payload.export_url)
        || getExpUrl(designData?.export_url)
        || assetData?.source_url
        || payload.source_url
        || payload.url;
      const canvasInfo = payload.canvas || designData?.canvas || {
        width: assetData?.width,
        height: assetData?.height,
      };
      const assetId = payload.asset_id || assetData?.asset_id;
      const layerId = payload.design_id || designData?.design_id || assetId || 'main-design';
      const layerName = designData?.title || 'Generated Design';

      const canvas = fabricCanvasRef.current;
      if (!canvas) {
        window.dispatchEvent(new CustomEvent('ai-generating', { detail: { status: false } }));
        toast.error('Editor canvas is not ready. Please try again.');
        dispatch(setIsRestoringDesign(false));
        return;
      }

      logicalSizeRef.current = {
        width: canvasInfo?.width || DEFAULT_LOGICAL_SIZE.width,
        height: canvasInfo?.height || DEFAULT_LOGICAL_SIZE.height,
      };
      applyViewportFit(canvas);

      dispatch(clearRegions());
      if (assetId) {
        dispatch(setCurrentAssetId(assetId));
      }
      const parsed = handleDesignResponse({
        ...designData,
        ...assetData,
        preview_url: previewUrl,
        canvas: canvasInfo,
        regions: assetData?.regions || designData?.regions || payload.regions,
      });
      allDesignRegionsRef.current = [...(parsed.regions || [])];
      const isRestore = !!payload.isRestore;

      const clearGeneratedObjects = () => {
        const objects = canvas.getObjects();
        const toRemove = [];
        for (const obj of objects) {
          const t = obj.get('layerType');
          if (isRestore || t === 'design' || t === 'region' || t === 'region-image' || t === 'region-tint') {
            if (t !== 'background' && obj.get('layerId') !== 'crop-rect') {
              toRemove.push(obj);
            }
          }
        }
        for (const obj of toRemove) {
          const id = obj.get('layerId') as string;
          canvas.remove(obj);
          if (id && !isRestore) dispatch(removeLayer(id));
        }
        if (isRestore) dispatch(setLayers([]));
      };

      const applyBackgroundFromLayers = () => {
        for (const obj of canvas.getObjects()) {
          if (obj.get('layerType') === 'background') {
            obj.set({ fill: 'transparent' });
          }
        }
      };

      const cacheRegionMasks = async () => {
        const logical = logicalSizeRef.current;
        regionMaskCacheRef.current = {};
        for (const region of parsed.regions) {
          if (seq !== designLoadSeq) return;
          if (!region.maskUrl) {
            console.log('[RegionMask]', {
              region_id: region.id,
              mask_url: region.maskUrl,
              mask_bounds: region.bounds,
              skipped: 'missing mask_url',
            });
            continue;
          }
          try {
            const result = await rasterizeRegionMask(region.maskUrl, logical);
            if (seq !== designLoadSeq) return;
            if (result.canvas) {
              regionMaskCacheRef.current[region.id] = result.canvas;
            }
            console.log('[RegionMask]', {
              region_id: region.id,
              mask_url: region.maskUrl,
              mask_bounds: region.bounds,
              dummy: result.dummy,
              covered: result.covered,
              cached: !!result.canvas,
              visible_on_canvas: false,
            });
          } catch (maskErr) {
            console.warn('[RegionMask] skipped:', region.id, maskErr);
          }
        }
        if (seq !== designLoadSeq) return;
        setMaskCacheVersion(incrementStateValue);
      };

      const safeCacheRegionMasks = async () => {
        try {
          await cacheRegionMasks();
        } catch (maskErr) {
          console.warn('[RegionMask] background cache failed', maskErr);
        }
      };

      const restoreTextLayer = (layerData: any, newReduxLayers: any[]) => {
        const textbox = new fabric.Textbox(layerData.text || DEFAULT_CANVAS_TEXT, {
          width: layerData.width || 640,
          fontSize: layerData.fontSize || 72,
          fontFamily: layerData.fontFamily || "Playfair Display",
          fill: layerData.fill || "#FFFFFF",
          textAlign: layerData.textAlign || "center",
          originX: 'left',
          originY: 'top',
          layerId: layerData.id,
          object_id: layerData.id,
          layerType: 'text',
          layerName: layerData.id,
          hasControls: true,
          hasBorders: true,
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
          lockScalingFlip: true,
          splitByGrapheme: true,
          minScaleLimit: 0.05,
          objectCaching: false,
          opacity: layerData.transform?.actual_opacity ?? layerData.opacity ?? 1,
          visible: layerData.transform?.actual_visible ?? true,
          globalCompositeOperation: layerData.blendMode || 'source-over',
        } as any);
        applyObjectToolState(textbox, activeToolRef.current);
        if (layerData.transform) {
          textbox.set({
            left: layerData.transform.left || 0,
            top: layerData.transform.top || 0,
            scaleX: layerData.transform.scale_x || 1,
            scaleY: layerData.transform.scale_y || 1,
            angle: layerData.transform.angle || 0,
          });
        }
        textbox.setCoords();
        canvas.add(textbox);
        bindTextEditingGuards(textbox);
        newReduxLayers.push({ layerId: layerData.id, layerType: 'text', layerName: 'Text', visible: layerData.transform?.actual_visible ?? true });
      };

      const restoreImageLayer = async (layerData: any, newReduxLayers: any[]) => {
        if (!layerData.src) return;
        try {
          const overlayImg = await loadFabricImage(layerData.src);
          overlayImg.set({
            layerId: layerData.id,
            object_id: layerData.id,
            layerType: 'image',
            layerName: layerData.id,
            mediaId: layerData.mediaId,
            mediaFileUrl: layerData.src,
            borderRadius: layerData.radius || 0,
            originX: 'left',
            originY: 'top',
            hasControls: true,
            hasBorders: true,
            lockMovementX: false,
            lockMovementY: false,
            lockScalingX: false,
            lockScalingY: false,
            lockRotation: false,
            opacity: layerData.transform?.actual_opacity ?? layerData.opacity ?? 1,
            visible: layerData.transform?.actual_visible ?? true,
            globalCompositeOperation: layerData.blendMode || 'source-over',
          } as any);
          if (layerData.transform) {
            overlayImg.set({
              left: layerData.transform.left || 0,
              top: layerData.transform.top || 0,
              scaleX: layerData.transform.scale_x || 1,
              scaleY: layerData.transform.scale_y || 1,
              angle: layerData.transform.angle || 0,
            });
          }
          stampObjectId(overlayImg, layerData.id);
          applyObjectToolState(overlayImg, activeToolRef.current);
          if (layerData.radius) applyImageBorderRadius(overlayImg, layerData.radius);
          overlayImg.setCoords();
          canvas.add(overlayImg);
          newReduxLayers.push({ layerId: layerData.id, layerType: 'image', layerName: 'Image', visible: layerData.transform?.actual_visible ?? true });
        } catch (err) {
          console.error('Failed to load overlay image', err);
        }
      };

      const processRestoreLayers = async (layers: any[], newReduxLayers: any[]) => {
        for (const layerData of layers) {
          if (layerData.type === 'design' || layerData.type === 'region') continue;
          if (layerData.type === 'text') {
            restoreTextLayer(layerData, newReduxLayers);
          } else if (layerData.type === 'image') {
            await restoreImageLayer(layerData, newReduxLayers);
          }
        }
      };

      const fetchAndPrepareBaseImage = async (url: string) => {
        let previewBytes = 0;
        let img: fabric.Image | null = null;
        try {
          const blob = await fetchPreviewBlob(url);
          if (seq !== designLoadSeq) return { img: null, previewBytes: 0 };
          previewBytes = blob.size;
          const blobUrl = URL.createObjectURL(blob);
          const htmlImg = await loadHtmlImage(blobUrl);
          const punched = knockOutBakedBackground(htmlImg, htmlImg.naturalWidth || htmlImg.width, htmlImg.naturalHeight || htmlImg.height);
          if (originalPreviewUrlRef.current && originalPreviewUrlRef.current.startsWith('blob:')) {
            URL.revokeObjectURL(originalPreviewUrlRef.current);
          }
          originalPreviewUrlRef.current = punched
            ? await canvasToBlobUrl(punched)
            : blobUrl;
          if (punched) URL.revokeObjectURL(blobUrl);
          img = await loadFabricImage(originalPreviewUrlRef.current);
          console.log('[Preview] baked background knocked out:', !!punched);
        } catch (fetchErr) {
          console.warn('[Canvas] preview fetch failed, falling back to fromURL:', fetchErr);
          originalPreviewUrlRef.current = url;
          img = await loadFabricImage(url);
        }
        return { img, previewBytes };
      };

      const setupBaseFabricImage = (img: fabric.Image, lw: number, lh: number) => {
        const imgW = img.width || lw;
        const imgH = img.height || lh;
        img.set({
          originX: 'left',
          originY: 'top',
          left: 0,
          top: 0,
          scaleX: lw / imgW,
          scaleY: lh / imgH,
          layerId,
          object_id: layerId,
          layerType: 'design',
          layerName,
          selectable: false,
          evented: false,
          hasControls: false,
        } as any);
        stampObjectId(img, layerId);
        applyObjectToolState(img, activeToolRef.current);
        img.setCoords();
        canvas.add(img);
      };

      const finalizeBaseDesignLoading = (newReduxLayers: any[], img: fabric.Image, lw: number, lh: number) => {
        if (parsed.regions && parsed.regions.length > 0) {
          dispatch(setDesignRegions(parsed.regions));
        }

        if (isRestore) {
          const restoredLayerIds: string[] = [];
          for (const layer of newReduxLayers) {
            restoredLayerIds.push(layer.layerId);
          }
          restackEditorLayers(canvas, restoredLayerIds);
          dispatch(setLayers(newReduxLayers as any));
        } else {
          const layerIds: string[] = [];
          for (const layer of layersRef.current) {
            layerIds.push(layer.layerId);
          }
          restackEditorLayers(canvas, layerIds);
          const generatedLayers: Layer[] = (parsed.regions && parsed.regions.length > 0)
            ? parsed.regions.map((r: any) => ({
                layerId: r.id,
                layerType: 'region' as const,
                layerName: r.name || 'Region',
                visible: true,
              }))
            : [{ layerId, layerType: 'design' as const, layerName }];
          dispatch(syncGeneratedLayers(generatedLayers));
        }

        window.dispatchEvent(new CustomEvent('ai-generating', { detail: { status: false } }));
        if (announceSuccess) {
          toast.success('Design regenerated successfully!');
        }
        if (shouldAnimate) {
          playGeneratedDesignEntrance(canvas, img, { width: lw, height: lh });
        } else {
          canvas.requestRenderAll();
        }
        console.log('[Canvas] base image added:', canvas.getObjects());
        resetHistoryBaselineRef.current();

        safeCacheRegionMasks();
      };

      const loadBaseDesignFromPreview = async (url: string) => {
        console.log('[Canvas] loading base image:', url);
        try {
          const { img, previewBytes } = await fetchAndPrepareBaseImage(url);
          if (seq !== designLoadSeq || !img) return;
          console.log('[Canvas] base image loaded:', img.width, img.height, 'bytes:', previewBytes);

          clearGeneratedObjects();
          applyBackgroundFromLayers();

          const { width: lw, height: lh } = logicalSizeRef.current;
          setupBaseFabricImage(img, lw, lh);

          let newReduxLayers: any[] = (parsed.regions && parsed.regions.length > 0)
            ? parsed.regions.map((r: any) => {
                const savedLayer = parsed.layers?.find((l: any) => (l.id || l.object_id) === r.id);
                return {
                  layerId: r.id,
                  layerType: 'region',
                  layerName: r.name || 'Region',
                  visible: savedLayer ? savedLayer.visible !== false : true,
                };
              })
            : [{ layerId, layerType: 'design', layerName }];
          if (isRestore && parsed.layers && parsed.layers.length > 0) {
            for (const layerData of parsed.layers) {
              if (layerData.type === 'design' || layerData.type === 'region') continue;
              if (layerData.type === 'text') {
                const textbox = new fabric.Textbox(layerData.text || DEFAULT_CANVAS_TEXT, {
                  width: layerData.width || 640,
                  fontSize: layerData.fontSize || 72,
                  fontFamily: layerData.fontFamily || "Playfair Display",
                  fill: layerData.fill || "#FFFFFF",
                  textAlign: layerData.textAlign || "center",
                  originX: 'left',
                  originY: 'top',
                  layerId: layerData.id,
                  object_id: layerData.id,
                  layerType: 'text',
                  layerName: layerData.id,
                  hasControls: true,
                  hasBorders: true,
                  lockMovementX: false,
                  lockMovementY: false,
                  lockScalingX: false,
                  lockScalingY: false,
                  lockRotation: false,
                  lockScalingFlip: true,
                  splitByGrapheme: true,
                  minScaleLimit: 0.05,
                  objectCaching: false,
                  opacity: layerData.opacity,
                  visible: layerData.visible,
                  globalCompositeOperation: layerData.blendMode || 'source-over',
                } as any);
                applyObjectToolState(textbox, activeToolRef.current);
                if (layerData.transform) {
                  textbox.set({
                    left: layerData.transform.left || 0,
                    top: layerData.transform.top || 0,
                    scaleX: layerData.transform.scale_x || 1,
                    scaleY: layerData.transform.scale_y || 1,
                    angle: layerData.transform.angle || 0,
                  });
                }
                textbox.setCoords();
                canvas.add(textbox);
                bindTextEditingGuards(textbox);
                newReduxLayers.push({ layerId: layerData.id, layerType: 'text', layerName: 'Text', visible: layerData.visible });
              } else if (layerData.type === 'image') {
                if (layerData.src) {
                  try {
                    const overlayImg = await loadFabricImage(layerData.src);
                    overlayImg.set({
                      layerId: layerData.id,
                      object_id: layerData.id,
                      layerType: 'image',
                      layerName: layerData.id,
                      mediaId: layerData.mediaId,
                      mediaFileUrl: layerData.src,
                      borderRadius: layerData.radius || 0,
                      originX: 'left',
                      originY: 'top',
                      hasControls: true,
                      hasBorders: true,
                      lockMovementX: false,
                      lockMovementY: false,
                      lockScalingX: false,
                      lockScalingY: false,
                      lockRotation: false,
                      opacity: layerData.opacity,
                      visible: layerData.visible,
                      globalCompositeOperation: layerData.blendMode || 'source-over',
                    } as any);
                    if (layerData.transform) {
                      overlayImg.set({
                        left: layerData.transform.left || 0,
                        top: layerData.transform.top || 0,
                        scaleX: layerData.transform.scale_x || 1,
                        scaleY: layerData.transform.scale_y || 1,
                        angle: layerData.transform.angle || 0,
                      });
                    }
                    stampObjectId(overlayImg, layerData.id);
                    applyObjectToolState(overlayImg, activeToolRef.current);
                    if (layerData.radius) applyImageBorderRadius(overlayImg, layerData.radius);
                    overlayImg.setCoords();
                    canvas.add(overlayImg);
                    newReduxLayers.push({ layerId: layerData.id, layerType: 'image', layerName: 'Image', visible: layerData.visible });
                  } catch (err) {
                    console.error('Failed to load overlay image', err);
                  }
                }
              }
            }
          }

          finalizeBaseDesignLoading(newReduxLayers, img, lw, lh);
        } catch (imgErr) {
          if (seq !== designLoadSeq) return;
          console.error('[Canvas] base image load failed:', imgErr);
          window.dispatchEvent(new CustomEvent('ai-generating', { detail: { status: false } }));
          toast.error('Failed to load generated design image.');
        } finally {
          dispatch(setIsRestoringDesign(false));
        }
      };

      if (!previewUrl) {
        console.error('[Canvas] No preview_url on update-main-design payload');
        window.dispatchEvent(new CustomEvent('ai-generating', { detail: { status: false } }));
        toast.error('Generated design is missing preview_url.');
        dispatch(setIsRestoringDesign(false));
        return;
      }
      await loadBaseDesignFromPreview(previewUrl);
    };
    window.addEventListener('update-main-design', handleUpdateMainDesign);

    const handleAddText = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { text, layerId, layerName } = customEvent.detail;
      const canvas = fabricCanvasRef.current;
      if (canvas) {
        const { width: lw, height: lh } = logicalSizeRef.current;
        const centerX = lw / 2;
        const centerY = lh / 2;

        const textbox = new fabric.Textbox(text || DEFAULT_CANVAS_TEXT, {
          width: Math.min(640, Math.max(240, lw * 0.45)),
          fontSize: 72,
          fontFamily: "Playfair Display",
          fill: "#FFFFFF",
          textAlign: "center",
          originX: 'center',
          originY: 'center',
          layerId,
          object_id: layerId,
          layerType: 'text',
          layerName,
          hasControls: true,
          hasBorders: true,
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
          lockScalingFlip: true,
          splitByGrapheme: true,
          minScaleLimit: 0.05,
          objectCaching: false,
        } as any);
        applyObjectToolState(textbox, 'cursor');

        textbox.setPositionByOrigin(new fabric.Point(centerX, centerY), 'center', 'center');
        textbox.setCoords();

        canvas.add(textbox);
        restackEditorLayers(canvas, layersRef.current.map((layer) => layer.layerId));
        if (activeToolRef.current !== 'cursor') {
          setActiveTool('cursor');
        }
        constrainObjectToArtboard(textbox, { width: lw, height: lh }, canvas);
        activateCanvasLayer(canvas, layerId, 'layers-panel', {
          dispatch,
          activeLayerIdRef,
          suppressSelectionClearedRef,
        });
        bindTextEditingGuards(textbox);
        canvas.requestRenderAll();
        updateReduxProps(textbox);
      }
    };
    window.addEventListener('add-fabric-text', handleAddText);

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field (like the sidebar text box)
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'Escape' && activeTool === 'crop') {
        setActiveTool('cursor');
      } else if (e.key === 'Enter' && activeTool === 'crop') {
        handleApplyCrop();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    const applyCanvasBasicProps = (obj: any, props: any) => {
      const basicProps = ['scale', 'angle', 'opacity', 'left', 'top', 'fontFamily', 'fill', 'textAlign', 'fontWeight', 'fontStyle'];
      for (const prop of basicProps) {
        if (props[prop] !== undefined) {
          if (prop === 'scale') obj.scale(props.scale);
          else obj.set(prop, props[prop]);
        }
      }
      if (props.blendMode !== undefined) obj.set('globalCompositeOperation', props.blendMode);
    };

    const applyCanvasTextContentProps = (obj: any, props: any) => {
      if (props.text !== undefined && !getTextContentError(props.text)) {
        obj.set('text', props.text);
        obj.set('layerName', props.text);
        const id = getObjectId(obj);
        if (id) {
          dispatch(updateLayer({ layerId: id, layerName: props.text }));
        }
        if (typeof (obj as any).initDimensions === 'function') {
          (obj as any).initDimensions();
        }
        rememberValidCanvasText(obj);
      }
    };

    const applyCanvasTextFormattingProps = (obj: any, props: any, logicalSize: any) => {
      if (isCanvasTextObject(obj)) {
        const limits = getTextStyleLimits(logicalSize);
        const clamped = clampTextStyleProps({
          fontSize: props.fontSize !== undefined ? props.fontSize : (obj as any).fontSize,
          charSpacing: props.charSpacing !== undefined ? props.charSpacing : (obj as any).charSpacing,
          lineHeight: props.lineHeight !== undefined ? props.lineHeight : (obj as any).lineHeight,
        }, limits);
        if (props.fontSize !== undefined) obj.set('fontSize', clamped.fontSize);
        if (props.charSpacing !== undefined) obj.set('charSpacing', clamped.charSpacing);
        if (props.lineHeight !== undefined) obj.set('lineHeight', clamped.lineHeight);

        if (typeof (obj as any).initDimensions === 'function') {
          (obj as any).initDimensions();
        }
      } else {
        if (props.fontSize !== undefined) obj.set('fontSize', props.fontSize);
        if (props.charSpacing !== undefined) obj.set('charSpacing', props.charSpacing);
        if (props.lineHeight !== undefined) obj.set('lineHeight', props.lineHeight);
      }
    };

    const applyCanvasObjectProps = (obj: any, props: any, logicalSize: any) => {
      applyCanvasBasicProps(obj, props);
      applyCanvasTextContentProps(obj, props);
      applyCanvasTextFormattingProps(obj, props, logicalSize);

      if (props.borderRadius !== undefined && isUploadImageLayer(obj)) {
        applyImageBorderRadius(obj, props.borderRadius);
      }
    };

    const handleUpdateProps = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { layerId, props } = customEvent.detail;
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      const obj = findCanvasObjectById(canvas, layerId);
      if (!obj) return;

      applyCanvasObjectProps(obj, props, logicalSizeRef.current);

      constrainObjectToArtboard(obj, logicalSizeRef.current, canvas);
      obj.setCoords();
      canvas.requestRenderAll();
      updateReduxProps(obj);
      commitHistoryDebouncedRef.current();
    };
    window.addEventListener('update-fabric-props', handleUpdateProps);

    const handlePositionOverlay = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { layerId, alignX, alignY } = customEvent.detail as {
        layerId: string;
        alignX: 'left' | 'center' | 'right';
        alignY: 'top' | 'center' | 'bottom';
      };
      const canvas = fabricCanvasRef.current;
      if (!canvas || !layerId) return;
      const obj = findCanvasObjectById(canvas, layerId);
      if (!obj || obj.get('layerType') === 'background') return;

      const { width: lw, height: lh } = logicalSizeRef.current;
      const originX = alignX === 'left' ? 'left' : alignX === 'right' ? 'right' : 'center';
      const originY = alignY === 'top' ? 'top' : alignY === 'bottom' ? 'bottom' : 'center';
      const x = alignX === 'left' ? 0 : alignX === 'right' ? lw : lw / 2;
      const y = alignY === 'top' ? 0 : alignY === 'bottom' ? lh : lh / 2;

      obj.setPositionByOrigin(new fabric.Point(x, y), originX, originY);
      obj.setCoords();
      constrainObjectToArtboard(obj, logicalSizeRef.current, canvas);
      canvas.setActiveObject(obj);
      canvas.requestRenderAll();
      updateReduxProps(obj);
      commitHistoryRef.current();
    };
    window.addEventListener('position-fabric-object', handlePositionOverlay);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('add-fabric-image', handleAddImage);
      window.removeEventListener('add-fabric-text', handleAddText);
      window.removeEventListener('update-fabric-props', handleUpdateProps);
      window.removeEventListener('position-fabric-object', handlePositionOverlay);
      window.removeEventListener('update-main-design', handleUpdateMainDesign);
      window.removeEventListener('keydown', handleKeyDown);
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      setIsCanvasReady(false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (urlDesignId && (!currentDesignRef.current || currentDesignRef.current.design_id !== urlDesignId)) {
      const fetchUrlDesign = async () => {
        try {
          dispatch(setIsRestoringDesign(true));
          const res = await getDesign(urlDesignId).unwrap();
          const data = res?.data || res;
          dispatch(setCurrentDesign(data));
          window.dispatchEvent(new CustomEvent('update-main-design', {
            detail: {
              designData: data,
              design_id: data.design_id,
              preview_url: data.preview_url,
              canvas: data.canvas,
              layers: data.layers,
              regions: data.regions,
              isRestore: true,
            }
          }));
          // Remove the query param so it doesn't refetch on every render
          navigate('/dashboard', { replace: true });
        } catch (err: any) {
          toast.error("Failed to load design from URL");
          dispatch(setIsRestoringDesign(false));
        }
      };
      fetchUrlDesign();
    }
  }, [urlDesignId, getDesign, dispatch, navigate]);

  // Keep the Fabric viewport fitted to the editor workspace
  useEffect(() => {
    if (!isCanvasReady || !containerRef.current) return;
    const workspace = containerRef.current;
    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        applyViewportFit();
      });
    });
    observer.observe(workspace);
    applyViewportFit();
    return () => observer.disconnect();
  }, [isCanvasReady]);

  useEffect(() => {
    if (!isCanvasReady) return;
    applyViewportFit();
  }, [isCanvasReady, zoomPercent]);

  useEffect(() => {
    if (!zoomMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!zoomMenuRef.current?.contains(e.target as Node)) setZoomMenuOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [zoomMenuOpen]);

  // Handle AI Loading Overlay
  useEffect(() => {
    const handleGenerating = (e: Event) => {
      const customEvent = e as CustomEvent;
      dispatch(setIsGenerating(customEvent.detail.status));
    };
    window.addEventListener('ai-generating', handleGenerating);
    return () => window.removeEventListener('ai-generating', handleGenerating);
  }, []);

  // Handle active tool logic (Hand vs Cursor)
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    (canvas as any).skipTargetFind = activeTool === 'hand';
    canvas.selection = activeTool !== 'hand';
    canvas.defaultCursor = activeTool === 'hand' ? 'grab' : activeTool === 'crop' ? 'crosshair' : 'default';
    if (activeTool === 'crop') {
      suppressSelectionClearedRef.current = true;
    }
    canvas.getObjects().forEach((obj) => applyObjectToolState(obj, activeTool));
    restackEditorLayers(canvas, layersRef.current.map((layer) => layer.layerId));
    if (activeTool === 'hand') canvas.discardActiveObject();
    canvas.requestRenderAll();
    if (activeTool === 'crop') {
      window.setTimeout(() => {
        suppressSelectionClearedRef.current = false;
      }, 0);
    }

    let isDragging = false;
    let lastPosX = 0;
    let lastPosY = 0;

    const onMouseDown = (opt: any) => {
      if (activeToolRef.current !== 'hand') return;
      const evt = opt.e;
      isDragging = true;
      canvas.selection = false;
      canvas.defaultCursor = 'grabbing';
      lastPosX = evt.clientX;
      lastPosY = evt.clientY;
    };

    const onMouseMove = (opt: any) => {
      if (!isDragging || activeToolRef.current !== 'hand') return;
      const e = opt.e;
      const vpt = canvas.viewportTransform;
      if (vpt) {
        vpt[4] += e.clientX - lastPosX;
        vpt[5] += e.clientY - lastPosY;
        canvas.setViewportTransform(vpt);
      }
      lastPosX = e.clientX;
      lastPosY = e.clientY;
    };

    const onMouseUp = () => {
      if (isDragging && activeToolRef.current === 'hand') {
        canvas.defaultCursor = 'grab';
      }
      isDragging = false;
    };

    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);

    return () => {
      canvas.off('mouse:down', onMouseDown);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:up', onMouseUp);
    };
  }, [activeTool]);

  // Handle grid rendering
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const renderGrid = (opt: any) => {
      if (!isGridVisible || suppressEditorGrid) return;
      const ctx = opt?.ctx || (canvas as any).contextContainer;
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const gridScreen = 64;
      const w = canvas.getWidth();
      const h = canvas.getHeight();
      ctx.strokeStyle = 'rgba(140, 227, 82, 0.35)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x <= w; x += gridScreen) {
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, h);
      }
      for (let y = 0; y <= h; y += gridScreen) {
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(w, y + 0.5);
      }
      ctx.stroke();
      ctx.restore();
    };

    canvas.on('after:render', renderGrid);
    canvas.requestRenderAll();

    return () => {
      canvas.off('after:render', renderGrid);
    };
  }, [isGridVisible]);

  // Handle Crop logic
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const clearCropRect = () => {
      canvas.getObjects().filter((obj) => obj.get('layerId') === 'crop-rect' || obj.get('layerType') === 'crop').forEach((obj) => canvas.remove(obj));
      cropRectRef.current = null;
    };

    const restoreCropTarget = (tool: ToolType) => {
      const previous = cropTargetRef.current;
      const targetId = cropTargetObjectIdRef.current;
      if (previous) {
        applyObjectToolState(previous, tool);
        if (tool === 'cursor' && isEditableImageLayer(previous)) {
          suppressSelectionClearedRef.current = true;
          canvas.setActiveObject(previous);
          commitActiveLayer(dispatch, activeLayerIdRef, getObjectId(previous));
          window.setTimeout(() => {
            suppressSelectionClearedRef.current = false;
          }, 0);
        }
      }
      cropTargetRef.current = null;
      cropTargetObjectIdRef.current = null;
      return targetId;
    };

    if (activeTool !== 'crop') {
      if (applyingCropRef.current) {
        applyingCropRef.current = false;
        clearCropRect();
        setCropState({ active: false, originalObj: null, cropRect: null, objectId: null });
        canvas.requestRenderAll();
        return;
      }
      if (cropState.active || cropRectRef.current) {
        console.log('[CropCancel]', { cropTargetObjectId: cropTargetObjectIdRef.current });
        clearCropRect();
        restoreCropTarget(activeTool);
        setCropState({ active: false, originalObj: null, cropRect: null, objectId: null });
        canvas.requestRenderAll();
      }
      return;
    }

    clearCropRect();

    const targetId = cropTargetObjectIdRef.current || activeLayerIdRef.current;
    const target = targetId ? findCanvasObjectById(canvas, targetId) : undefined;
    if (!isEditableImageLayer(target)) {
      cropTargetObjectIdRef.current = null;
      toast.info('Select an image to crop.');
      setActiveTool('cursor');
      return;
    }

    const cropTarget = target;
    const cropTargetObjectId = getObjectId(cropTarget);
    cropTargetObjectIdRef.current = cropTargetObjectId;
    console.log('[CropStart]', {
      activeLayerId: activeLayerIdRef.current,
      cropTargetObjectId,
      objectType: cropTarget.get('layerType'),
    });

    cropTarget.set({ selectable: false, evented: false });
    cropTarget.setCoords();

    const box = getObjectSceneBox(cropTarget);
    const zoom = Math.max(canvas.getZoom(), 0.05);

    const cropRect = new fabric.Rect({
      left: box.left,
      top: box.top,
      originX: 'left',
      originY: 'top',
      width: box.width,
      height: box.height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      fill: 'rgba(12, 20, 28, 0.12)',
      stroke: '#8CE352',
      strokeWidth: 2 / zoom,
      strokeUniform: true,
      strokeDashArray: [16 / zoom, 10 / zoom],
      cornerColor: '#8CE352',
      cornerStrokeColor: '#0c141c',
      cornerSize: 20,
      touchCornerSize: 36,
      transparentCorners: false,
      hasBorders: true,
      hasControls: true,
      lockRotation: true,
      lockScalingFlip: true,
      selectable: true,
      evented: true,
      objectCaching: false,
      hoverCursor: 'move',
      moveCursor: 'move',
      layerId: 'crop-rect',
      object_id: 'crop-rect',
      layerType: 'crop',
    } as any);
    cropRect.setControlsVisibility({ mtr: false });

    cropRect.on('modified', () => constrainCropRectToImage(cropRect, cropTarget));
    cropRect.on('moving', () => constrainCropRectToImage(cropRect, cropTarget));

    canvas.add(cropRect);
    if (typeof canvas.bringObjectToFront === 'function') {
      canvas.bringObjectToFront(cropRect);
    }
    cropRect.setCoords();
    constrainCropRectToImage(cropRect, cropTarget);

    suppressSelectionClearedRef.current = true;
    canvas.setActiveObject(cropRect);
    commitActiveLayer(dispatch, activeLayerIdRef, cropTargetObjectId);
    window.setTimeout(() => {
      suppressSelectionClearedRef.current = false;
    }, 0);
    canvas.requestRenderAll();

    cropRectRef.current = cropRect;
    cropTargetRef.current = cropTarget;
    setCropState({ active: true, originalObj: cropTarget, cropRect, objectId: cropTargetObjectId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool]); // NOTE: Intentionally excluding cropState to prevent loop

  useEffect(() => {
    const handleSave = (e: Event) => {
      const status = (e as CustomEvent).detail?.status || 'Draft';
      saveCurrentDesign(status);
    };

    const handleSaveFinal = async () => {
      const designId = currentDesignRef.current?.design_id;
      if (!designId) {
        toast.error("Please generate a design first!");
        return;
      }
      try {
        await saveCurrentDesign('Draft', { silent: true });
        toast.info("Saving Final Design...");
        const res = await saveDesignFinalApi(designId).unwrap();
        toast.success(res?.message || "Design saved successfully!");
      } catch (err: any) {
        toast.error(err?.data?.message || err?.message || "Failed to save design.");
      }
    };
    const handleToggleVisibility = (e: Event) => {
      const layerId = (e as CustomEvent).detail?.layerId;
      const canvas = fabricCanvasRef.current;
      if (!canvas || !layerId) return;

      if (regionsRef.current.some((r) => r.id === layerId)) {
        const curLayer = layersRef.current.find((l) => l.layerId === layerId);
        const nextVisible = curLayer ? curLayer.visible === false : false;
        dispatch(setLayerVisible({ layerId, visible: nextVisible }));
        commitHistoryRef.current();
        return;
      }

      const obj = findCanvasObjectById(canvas, layerId);
      if (!obj || obj.get('layerType') === 'background') return;
      const nextVisible = obj.visible === false;
      obj.set({ visible: nextVisible, dirty: true });
      dispatch(setLayerVisible({ layerId, visible: nextVisible }));
      canvas.requestRenderAll();
      commitHistoryRef.current();
    };
    const handleReorderLayers = (e: Event) => {
      const order = (e as CustomEvent).detail?.order as string[] | undefined;
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      restackEditorLayers(canvas, order || layersRef.current.map((layer) => layer.layerId));
      canvas.requestRenderAll();
      commitHistoryRef.current();
    };

    const handleSelectObject = (e: Event) => {
      const layerId = (e as CustomEvent).detail?.layerId;
      const canvas = fabricCanvasRef.current;
      if (!canvas || !layerId) return;
      if (activeToolRef.current === 'crop' && cropTargetObjectIdRef.current) {
        if (layerId !== cropTargetObjectIdRef.current) {
          dispatch(setActiveLayerId(cropTargetObjectIdRef.current));
          activeLayerIdRef.current = cropTargetObjectIdRef.current;
          toast.info('Finish or cancel crop before switching layers.');
        }
        return;
      }
      if (regionsRef.current.some((r) => r.id === layerId)) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        commitActiveLayer(dispatch, activeLayerIdRef, layerId);
        dispatch(setSelectedObjectProps(null));
        return;
      }
      activateCanvasLayer(canvas, layerId, 'layers-panel', {
        dispatch,
        activeLayerIdRef,
        suppressSelectionClearedRef,
      });
    };

    const handleExportCanvas = async (e: Event) => {
      const rawFormat = String((e as CustomEvent).detail?.format || 'png').toLowerCase();
      const ext = rawFormat === 'jpeg' ? 'jpg' : rawFormat;
      const canvas = fabricCanvasRef.current;
      const prevActive = canvas?.getActiveObject();
      try {
        if (!canvas) throw new Error('Canvas is not ready.');

        const exportable = canvas.getObjects().filter((obj) => {
          const t = String(obj.get('layerType') || '');
          return t === 'image' || t === 'text' || t === 'design';
        });
        if (!exportable.length) {
          toast.error('Upload an image or generate a design first.');
          return;
        }

        await saveCurrentDesign('Draft', { silent: true });

        const hiddenCrop = canvas.getObjects().filter((obj) => String(obj.get('layerType') || '') === 'crop' || getObjectId(obj) === 'crop-rect');
        hiddenCrop.forEach((obj) => obj.set('visible', false));
        canvas.discardActiveObject();

        const logical = logicalSizeRef.current;
        const designId = currentDesignRef.current?.design_id || 'design';

        // Trigger server export in background for record keeping
        try {
          if (designId && !designId.startsWith('static-')) {
            exportDesignApi({
              designId: designId,
              format: rawFormat
            });
          }
        } catch (serverErr) {
          console.warn('[Export] Backend export API call:', serverErr);
        }

        // Always perform direct Fabric canvas export to preserve customized region colors and layer modifications
        if (rawFormat === 'svg') {
          const vpt = canvas.viewportTransform ? [...canvas.viewportTransform] : [1, 0, 0, 1, 0, 0];
          canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
          canvas.renderAll();

          const svgString = canvas.toSVG({
            width: logical.width,
            height: logical.height,
            viewBox: { x: 0, y: 0, width: logical.width, height: logical.height },
          } as any);

          canvas.setViewportTransform(vpt as any);
          const dlBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          triggerBlobDownload(dlBlob, safeDownloadName(designId, 'svg'));
          toast.success('SVG downloaded successfully!');
        } else {
          const rasterFormat: 'png' | 'jpeg' = ext === 'jpg' ? 'jpeg' : 'png';
          const vpt = canvas.viewportTransform ? [...canvas.viewportTransform] : [1, 0, 0, 1, 0, 0];
          canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
          canvas.renderAll();

          let exportDataUrl = '';
          if (rasterFormat === 'jpeg') {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = logical.width;
            tempCanvas.height = logical.height;
            const ctx = tempCanvas.getContext('2d');
            if (ctx) {
              ctx.fillStyle = JPEG_EXPORT_BG;
              ctx.fillRect(0, 0, logical.width, logical.height);
              const pngData = canvas.toDataURL({
                format: 'png',
                quality: 1,
                left: 0,
                top: 0,
                width: logical.width,
                height: logical.height,
                filter: (obj: fabric.Object) => {
                  const t = String(obj.get('layerType') || '');
                  const id = String(obj.get('object_id') || obj.get('layerId') || '');
                  if (id === 'crop-rect' || t === 'crop' || t === 'background') return false;
                  return obj.visible !== false;
                },
              } as any);
              const imgEl = new Image();
              await new Promise<void>((resolve) => {
                imgEl.onload = () => {
                  ctx.drawImage(imgEl, 0, 0);
                  resolve();
                };
                imgEl.src = pngData;
              });
              exportDataUrl = tempCanvas.toDataURL('image/jpeg', 0.95);
            } else {
              exportDataUrl = canvas.toDataURL({
                format: 'jpeg',
                quality: 0.95,
                left: 0,
                top: 0,
                width: logical.width,
                height: logical.height,
                filter: (obj: fabric.Object) => {
                  const t = String(obj.get('layerType') || '');
                  const id = String(obj.get('object_id') || obj.get('layerId') || '');
                  if (id === 'crop-rect' || t === 'crop' || t === 'background') return false;
                  return obj.visible !== false;
                },
              } as any);
            }
          } else {
            exportDataUrl = canvas.toDataURL({
              format: 'png',
              quality: 1,
              left: 0,
              top: 0,
              width: logical.width,
              height: logical.height,
              filter: (obj: fabric.Object) => {
                const t = String(obj.get('layerType') || '');
                const id = String(obj.get('object_id') || obj.get('layerId') || '');
                if (id === 'crop-rect' || t === 'crop' || t === 'background') return false;
                return obj.visible !== false;
              },
            } as any);
          }

          canvas.setViewportTransform(vpt as any);
          const dlBlob = dataUrlToBlob(exportDataUrl);
          triggerBlobDownload(dlBlob, safeDownloadName(designId, ext));
          toast.success(`${rawFormat.toUpperCase()} downloaded successfully!`);
        }
      } catch (err: any) {
        console.error('[Export]', err);
        toast.error(err?.message || `Failed to download ${ext.toUpperCase()}`);
      } finally {
        const canvasNow = fabricCanvasRef.current;
        canvasNow?.getObjects()
          .filter((obj) => String(obj.get('layerType') || '') === 'crop' || getObjectId(obj) === 'crop-rect')
          .forEach((obj) => obj.set('visible', true));
        if (canvasNow && prevActive && canvasNow.getObjects().includes(prevActive)) {
          canvasNow.setActiveObject(prevActive);
        }
        canvasNow?.requestRenderAll();
        window.dispatchEvent(new CustomEvent('export-canvas-complete'));
      }
    };
    const handleRemoveObject = (e: Event) => {
      const layerId = (e as CustomEvent).detail?.layerId;
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const targetId = layerId || (activeLayerIdRef.current && regionsRef.current.some((r) => r.id === activeLayerIdRef.current) ? activeLayerIdRef.current : undefined);

      if (targetId && regionsRef.current.some((r) => r.id === targetId)) {
        dispatch(removeRegion(targetId));
        dispatch(removeLayer(targetId));
        if (activeLayerIdRef.current === targetId) {
          commitActiveLayer(dispatch, activeLayerIdRef, null);
        }
        commitHistoryRef.current();
        queueAutosave();
        return;
      }

      const objectsToRemove: fabric.Object[] = [];
      if (layerId) {
        const obj = findCanvasObjectById(canvas, layerId);
        if (obj) objectsToRemove.push(obj);
      } else {
        const activeObj = canvas.getActiveObject();
        if (activeObj) {
          if (activeObj.type === 'activeSelection') {
            (activeObj as fabric.Group).getObjects().forEach((o) => objectsToRemove.push(o));
          } else {
            objectsToRemove.push(activeObj);
          }
        }
      }

      let removedAny = false;
      objectsToRemove.forEach((obj) => {
        const t = String(obj.get('layerType') || '');
        const id = getObjectId(obj);
        if (t === 'background' || t === 'design' || t === 'crop' || id === 'crop-rect') return;

        canvas.remove(obj);
        if (id) {
          dispatch(removeLayer(id));
          if (activeLayerIdRef.current === id) {
            commitActiveLayer(dispatch, activeLayerIdRef, null);
          }
        }
        removedAny = true;
      });

      if (removedAny) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        commitHistoryRef.current();
        queueAutosave();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const activeObj = canvas.getActiveObject();
      if (activeObj) {
        if ((activeObj as any).isEditing) return;
        e.preventDefault();
        handleRemoveObject(new CustomEvent('remove-fabric-object', { detail: {} }));
        return;
      }

      const activeId = activeLayerIdRef.current;
      if (activeId && regionsRef.current.some((r) => r.id === activeId)) {
        e.preventDefault();
        handleRemoveObject(new CustomEvent('remove-fabric-object', { detail: { layerId: activeId } }));
      }
    };

    window.addEventListener('save-current-design', handleSave);
    window.addEventListener('save-final-design', handleSaveFinal);
    window.addEventListener('toggle-fabric-visibility', handleToggleVisibility);
    window.addEventListener('reorder-fabric-layers', handleReorderLayers);
    window.addEventListener('select-fabric-object', handleSelectObject);
    window.addEventListener('export-canvas', handleExportCanvas);
    window.addEventListener('remove-fabric-object', handleRemoveObject);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('save-current-design', handleSave);
      window.removeEventListener('save-final-design', handleSaveFinal);
      window.removeEventListener('toggle-fabric-visibility', handleToggleVisibility);
      window.removeEventListener('reorder-fabric-layers', handleReorderLayers);
      window.removeEventListener('select-fabric-object', handleSelectObject);
      window.removeEventListener('export-canvas', handleExportCanvas);
      window.removeEventListener('remove-fabric-object', handleRemoveObject);
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !isCanvasReady) return;
    const onModified = () => queueAutosave();
    canvas.on('object:modified', onModified);
    return () => {
      canvas.off('object:modified', onModified);
    };
  }, [isCanvasReady]);

  async function handleApplyCrop() {
    const canvas = fabricCanvasRef.current;
    const img = cropTargetRef.current || cropState.originalObj;
    const rect = cropRectRef.current || cropState.cropRect;
    const targetId = cropTargetObjectIdRef.current || cropState.objectId || getObjectId(img);
    if (!canvas || !img || !rect || !targetId) return;
    if (getObjectId(img) !== targetId) {
      toast.error('Crop target no longer matches the selected image.');
      return;
    }

    console.log('[CropApply]', {
      cropTargetObjectId: targetId,
      activeLayerId: activeLayerIdRef.current,
    });

    constrainCropRectToImage(rect, img);
    suppressSelectionClearedRef.current = true;
    try {
      await applyFabricCrop(canvas, img, rect);
    } catch (err) {
      suppressSelectionClearedRef.current = false;
      console.error('Crop failed', err);
      rect.set({ visible: true });
      canvas.setActiveObject(rect);
      canvas.requestRenderAll();
      toast.error('Could not crop this image.');
      return;
    }

    canvas.remove(rect);
    cropRectRef.current = null;
    cropTargetRef.current = null;
    cropTargetObjectIdRef.current = null;
    applyObjectToolState(img, 'cursor');
    stampObjectId(img, targetId);
    applyingCropRef.current = true;
    setCropState({ active: false, originalObj: null, cropRect: null, objectId: null });
    setActiveTool('cursor');
    suppressSelectionClearedRef.current = true;
    canvas.setActiveObject(img);
    commitActiveLayer(dispatch, activeLayerIdRef, targetId);
    window.setTimeout(() => {
      suppressSelectionClearedRef.current = false;
    }, 0);
    restackEditorLayers(canvas, layersRef.current.map((layer) => layer.layerId));
    canvas.requestRenderAll();
    console.log('[CropComplete]', { objectId: getObjectId(img) });
    queueAutosave();
    commitHistoryRef.current();
  }

  const handleToggleCrop = () => {
    if (activeTool === 'crop') {
      setActiveTool('cursor');
      return;
    }
    const canvas = fabricCanvasRef.current;
    const activeId = activeLayerIdRef.current;
    if (!canvas || !activeId) {
      toast.info('Select an image to crop.');
      return;
    }
    const obj = findCanvasObjectById(canvas, activeId);
    const layerType = obj ? String(obj.get('layerType') || '') : '';
    if (!isEditableImageLayer(obj)) {
      if (layerType === 'text') {
        toast.info('Crop works on images, not text.');
      } else {
        toast.info('Select an image to crop.');
      }
      return;
    }
    cropTargetObjectIdRef.current = getObjectId(obj);
    setActiveTool('crop');
  };

  const toggleFullScreen = () => {
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      try {
        setPreviewSrc(canvas.toDataURL({ format: 'png', quality: 1, multiplier: 1 }));
      } catch (err) {
        console.warn('Fullscreen export failed', err);
        setPreviewSrc(currentDesign?.preview_url || null);
      }
    } else {
      setPreviewSrc(currentDesign?.preview_url || null);
    }
    setIsPreviewOpen(true);
  };

  useEffect(() => {
    if (!isPreviewOpen) return;
    const { body, documentElement } = document;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = documentElement.style.overflow;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    const stopScroll = (e: Event) => {
      e.preventDefault();
    };
    window.addEventListener('wheel', stopScroll, { passive: false });
    window.addEventListener('touchmove', stopScroll, { passive: false });
    return () => {
      body.style.overflow = prevBodyOverflow;
      documentElement.style.overflow = prevHtmlOverflow;
      window.removeEventListener('wheel', stopScroll);
      window.removeEventListener('touchmove', stopScroll);
    };
  }, [isPreviewOpen]);

  return (
    <>
      {isPreviewOpen && (
        <div
          className="editor_fullscreen_overlay"
          onClick={() => setIsPreviewOpen(false)}
          onWheel={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="editor_fullscreen_close"
            onClick={() => setIsPreviewOpen(false)}
          >
            &times;
          </button>
          {previewSrc && (
            <img
              src={previewSrc}
              alt="Fullscreen preview"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}

      <div className="editor_toolbar">
        <div className="toolbar_left">
          <div className="tool_group tool_group_history">
            <Tooltip title="Undo (Ctrl+Z)" {...editorTooltipProps}>
              <span>
                <button
                  type="button"
                  className="history_btn"
                  onClick={handleUndo}
                  disabled={!canUndo}
                  aria-label="Undo"
                >
                  <img src="/static/images/undo.svg" alt="" />
                  <span>Undo</span>
                </button>
              </span>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Y)" {...editorTooltipProps}>
              <span>
                <button
                  type="button"
                  className="history_btn"
                  onClick={handleRedo}
                  disabled={!canRedo}
                  aria-label="Redo"
                >
                  <img src="/static/images/redo.svg" alt="" />
                  <span>Redo</span>
                </button>
              </span>
            </Tooltip>
          </div>
          <div className={`zoom_control ${zoomMenuOpen ? 'is_open' : ''}`} ref={zoomMenuRef}>
            <button
              type="button"
              className="zoom_chip"
              onClick={() => setZoomMenuOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={zoomMenuOpen}
            >
              <span>{zoomPercent}%</span>
              <KeyboardArrowDownIcon />
            </button>
            {zoomMenuOpen && (
              <div className="zoom_menu" role="listbox">
                {ZOOM_OPTIONS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="option"
                    aria-selected={zoomPercent === value}
                    className={`zoom_option ${zoomPercent === value ? 'active' : ''}`}
                    onClick={() => {
                      setZoomPercent(value);
                      setZoomMenuOpen(false);
                    }}
                  >
                    {value}%
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="tool_group tool_group_center">
          <Tooltip title="Pan" {...editorTooltipProps}>
            <button type="button" className={`tool_btn ${activeTool === 'hand' ? 'active' : ''}`} onClick={() => setActiveTool('hand')}>
              <img src="/static/images/hand.svg" alt="Pan" />
            </button>
          </Tooltip>
          <Tooltip title="Select" {...editorTooltipProps}>
            <button type="button" className={`tool_btn ${activeTool === 'cursor' ? 'active' : ''}`} onClick={() => setActiveTool('cursor')}>
              <img src="/static/images/cursor_arrow.svg" alt="Select" />
            </button>
          </Tooltip>
          <Tooltip title="Crop" {...editorTooltipProps}>
            <button
              type="button"
              className={`tool_btn ${activeTool === 'crop' ? 'active' : ''}`}
              onClick={handleToggleCrop}
            >
              <img src="/static/images/crop.svg" alt="Crop" />
            </button>
          </Tooltip>
          <Tooltip title="Grid" {...editorTooltipProps}>
            <button type="button" className={`tool_btn ${isGridVisible ? 'active' : ''}`} onClick={() => setIsGridVisible(!isGridVisible)}>
              <img src="/static/images/grid.svg" alt="Grid" />
            </button>
          </Tooltip>
        </div>

        <div className="tool_group tool_group_right">
          <span className="grid_status_dot"></span>
          Grid
          <Tooltip title="Fullscreen" {...editorTooltipProps}>
            <button type="button" className="tool_btn" onClick={toggleFullScreen}>
              <img src="/static/images/resize.svg" alt="Fullscreen" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className={`editor_card canvas_container ${cropState.active ? 'is-cropping' : ''}`}>
        <div ref={containerRef} className="canvas_workspace"></div>

        {!isGenerating && !isRestoringDesign && !currentDesign?.design_id && !layers.some((layer) => layer.layerType === 'text' || layer.layerType === 'image' || layer.layerType === 'design') && (
          <div className="canvas_empty_hint">
            <p className="canvas_empty_hint__title">Create something uniquely yours.</p>
            <p className="canvas_empty_hint__subtitle">Generate a one-of-a-kind symmetrical design</p>
          </div>
        )}

        {isGenerating && (
          <div style={{
            position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, zIndex: 40,
            background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            borderRadius: '8px', color: '#8CE352'
          }}>
            <style>{`
                @keyframes pulse {
                  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(140, 227, 82, 0.7); }
                  70% { transform: scale(1); box-shadow: 0 0 0 20px rgba(140, 227, 82, 0); }
                  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(140, 227, 82, 0); }
                }
              `}</style>
            <div style={{
              width: 60, height: 60, borderRadius: '50%', background: '#8CE352',
              animation: 'pulse 2s infinite', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <AutoAwesomeIcon style={{ color: '#000', fontSize: 32 }} />
            </div>
            <h3 style={{ margin: 0, letterSpacing: '1px' }}>Crafting your design...</h3>
            <p style={{ fontSize: '12px', color: '#ccc', marginTop: '8px' }}>Loading the design image...</p>
          </div>
        )}

        {cropState.active && (
          <div style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 10,
            zIndex: 100,
            background: 'rgba(0,0,0,0.85)',
            padding: '8px 12px',
            borderRadius: 8,
          }}>
            <button
              onClick={handleApplyCrop}
              style={{ background: '#8CE352', color: '#000', border: 'none', padding: '6px 16px', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold' }}
            >
              Apply Crop
            </button>
            <button
              onClick={() => setActiveTool('cursor')}
              style={{ background: '#333', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: 4, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="editor_card recent_designs">
        <div className="recent_header">
          <h3>Recent Designs</h3>
          <Link to="/recent-designs" className="view_all">View All Designs &rarr;</Link>
        </div>

        <div className="recent_gallery">
          {recentDesigns.length > 0 ? (
            recentDesigns.map((item: any) => {
              const isActive = currentDesign?.design_id === item.design_id;
              const expUrl = typeof item.export_url === 'object' && item.export_url ? (item.export_url.png || item.export_url.jpg || item.export_url.svg) : (typeof item.export_url === 'string' ? item.export_url : undefined);
              const thumbUrl = item.preview_url || expUrl || item.img;
              const thumb = thumbUrl ? `${thumbUrl}?t=${new Date(item.updated_at || item.created_at || Date.now()).getTime()}` : '/static/images/design1.png';
              return (
                <figure
                  key={item.design_id}
                  className={isActive ? "active" : ""}
                  onClick={() => (item.preview_url || item.design_id) ? openRecentDesign(item) : undefined}
                >
                  <img
                    src={thumb}
                    className="thumbnail"
                    alt={item.design_id}
                    onError={(e) => {
                      const el = e.currentTarget;
                      if (el.dataset.fallback === '1') return;
                      el.dataset.fallback = '1';
                      el.src = designs[1];
                    }}
                  />
                  <button
                    type="button"
                    className="delete_recent_btn"
                    title="Delete design"
                    disabled={deletingDesignId === item.design_id}
                    onClick={(e) => handleDeleteRecent(e, item)}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </button>
                </figure>
              );
            })
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.5)', padding: '20px', width: '100%', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', gridColumn: '1 / -1' }}>
              No Recent Designs
            </div>
          )}
        </div>
      </div>
      <Loader isLoad={isUpdatingDesign || isSavingDesign || isRestoringDesign || isSavingFinal} />
    </>
  );
};

export default Dashboard;
